import { StatusTreeSelect } from '../components/StatusTreeSelect';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import {
  RefreshCw,
  Clock,
  Save,
  Loader2,
  AlertCircle,
  CheckCircle,
  ChevronRight,
  Store,
  ChevronUp,
  ChevronDown,
  GripVertical,
  Eye,
  Sliders,
  Settings,
  MapPin,
  ClipboardList,
  Send,
  Users,
  RotateCcw,
  Mail,
  CreditCard,
  Trash2,
  Copy,
  FileText,
  Globe,
  Plus,
  Edit3,
  X,
  XCircle,
  ShieldCheck,
  AtSign,
  Phone,
  Package as PackageIcon,
  PackagePlus,
  AlertTriangle,
} from 'lucide-react';
import { SkullIcon } from '../components/icons/SkullIcon';
import { Button, buttonVariants } from '../components/ui/Button';
import { PageHeader } from '../components/ui/PageHeader';
import { Toggle } from '../components/ui/Toggle';
import { Select } from '../components/ui/Select';
import type { LucideIcon } from 'lucide-react';
import api from '../lib/api';
import { emailApi, storeApi, type EmailTemplate, type Store as StoreType, type StoreEmailConfig } from '../lib/api';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { withPlainTextPaste } from '../lib/quillPlainTextPaste';
import { EmptyState } from '../components/EmptyState';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';

interface SyncSettings {
  autoSyncEnabled: boolean;
  syncIntervalMinutes: number;
  syncOnStartup: boolean;
}

const SYNC_INTERVALS = [
  { value: 5, label: '5 minut' },
  { value: 10, label: '10 minut' },
  { value: 15, label: '15 minut' },
  { value: 30, label: '30 minut' },
  { value: 60, label: '1 godzina' },
  { value: 120, label: '2 godziny' },
  { value: 360, label: '6 godzin' },
  { value: 720, label: '12 godzin' },
  { value: 1440, label: '24 godziny' },
];

interface StoreBasic {
  id: string;
  name: string;
  url: string;
  isActive: boolean;
}

interface ToolbarIcons {
  showStatusChange: boolean;
  showLabels: boolean;
  showInvoices: boolean;
}

// ─── Shared toast notification (fixed bottom) ────────────────────────────────
function SettingsToast({ message, type, onClose }: { message: string; type: 'success' | 'error'; onClose: () => void }) {
  useEffect(() => {
    if (!message) return;
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [message, onClose]);

  if (!message) return null;

  return (
    <div
      className="fixed bottom-6 right-6 z-50 animate-fade-in transition-all duration-300"
    >
      <div
        className={`flex items-center gap-3 px-5 py-3 rounded-xl shadow-2xl border backdrop-blur-sm ${
          type === 'success'
            ? 'bg-success/15 border-success/30 text-success'
            : 'bg-danger/15 border-danger/30 text-danger'
        }`}
      >
        {type === 'success' ? (
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
        ) : (
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
        )}
        <span className="text-sm font-medium whitespace-nowrap">{message}</span>
        <button onClick={onClose} className="ml-2 opacity-60 hover:opacity-100 transition-opacity text-lg leading-none">&times;</button>
      </div>
    </div>
  );
}

function useSettingsToast() {
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const showSuccess = useCallback((msg: string) => setToast({ message: msg, type: 'success' }), []);
  const showError = useCallback((msg: string) => setToast({ message: msg, type: 'error' }), []);
  const clearToast = useCallback(() => setToast(null), []);
  return { toast, showSuccess, showError, clearToast };
}

function GeneralSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [savingDisplay, setSavingDisplay] = useState(false);
  const [savingToolbar, setSavingToolbar] = useState(false);
  const { toast, showSuccess, showError, clearToast } = useSettingsToast();
  const [orderedStores, setOrderedStores] = useState<StoreBasic[]>([]);
  const [showUnassignedStatus, setShowUnassignedStatus] = useState(false);
  const [toolbarIcons, setToolbarIcons] = useState<ToolbarIcons>({
    showStatusChange: true,
    showLabels: true,
    showInvoices: true,
  });

  // Load stores and store order
  useEffect(() => {
    const loadData = async () => {
      try {
        const [storesRes, settingsRes] = await Promise.all([
          api.get<StoreBasic[]>('/stores'),
          api.get<{ storeOrder: string[]; showUnassignedStatus: boolean; toolbarIcons: ToolbarIcons }>('/settings'),
        ]);

        const allStores = storesRes.data;
        const storeOrder = settingsRes.data.storeOrder || [];
        setShowUnassignedStatus(settingsRes.data.showUnassignedStatus ?? false);
        if (settingsRes.data.toolbarIcons) {
          setToolbarIcons(settingsRes.data.toolbarIcons);
        }

        // Sort stores according to saved order
        const orderedList: StoreBasic[] = [];
        const unorderedStores: StoreBasic[] = [];

        for (const storeId of storeOrder) {
          const store = allStores.find(s => s.id === storeId);
          if (store) {
            orderedList.push(store);
          }
        }

        for (const store of allStores) {
          if (!storeOrder.includes(store.id)) {
            unorderedStores.push(store);
          }
        }

        setOrderedStores([...orderedList, ...unorderedStores]);
      } catch (err) {
        console.error('Failed to load data:', err);
        showError('Nie udało się wczytać danych');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const moveStore = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= orderedStores.length) return;

    const newOrder = [...orderedStores];
    [newOrder[index], newOrder[newIndex]] = [newOrder[newIndex], newOrder[index]];
    setOrderedStores(newOrder);
  };

  const handleSave = async () => {
    setSaving(true);

    try {
      const storeOrder = orderedStores.map(s => s.id);
      await api.put('/settings/store-order', { storeOrder });
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDisplaySettings = async (value: boolean) => {
    setSavingDisplay(true);
    setShowUnassignedStatus(value);

    try {
      await api.put('/settings/display', { showUnassignedStatus: value });
      showSuccess('Ustawienia wyświetlania zostały zapisane');
    } catch {
      showError('Nie udało się zapisać ustawień');
      setShowUnassignedStatus(!value); // Revert on error
    } finally {
      setSavingDisplay(false);
    }
  };

  const handleSaveToolbarSettings = async (key: keyof ToolbarIcons, value: boolean) => {
    setSavingToolbar(true);
    const previousValue = toolbarIcons[key];
    setToolbarIcons(prev => ({ ...prev, [key]: value }));

    try {
      await api.put('/settings/toolbar-icons', { ...toolbarIcons, [key]: value });
      showSuccess('Ustawienia paska narzędzi zostały zapisane');
    } catch {
      showError('Nie udało się zapisać ustawień');
      setToolbarIcons(prev => ({ ...prev, [key]: previousValue })); // Revert on error
    } finally {
      setSavingToolbar(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-accent-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader icon={Sliders} title="Ustawienia ogólne" subtitle="Konfiguruj ogólne działanie systemu" />

      {/* Display Settings Section */}
      <div className="card">
        <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
          <Eye className="w-5 h-5 text-accent-primary" />
          Ustawienia wyświetlania
        </h3>

        <div className="flex items-center justify-between p-4 bg-surface-hover/30 rounded-xl">
          <div>
            <h4 className="text-text-primary font-medium">Pokaż status "Nieprzypisany"</h4>
            <p className="text-sm text-text-secondary">
              Wyświetlaj globalny status systemowy "Nieprzypisany" na liście statusów w panelu zamówień
            </p>
          </div>
          <Toggle
            checked={showUnassignedStatus}
            onChange={(e) => handleSaveDisplaySettings(e.target.checked)}
            disabled={savingDisplay}
          />
        </div>

        {/* Toolbar Icons Visibility */}
        <div className="mt-4 pt-4 border-t border-border-subtle">
          <h4 className="text-text-primary font-medium mb-3">Pasek narzędzi zamówień</h4>
          <p className="text-sm text-text-secondary mb-4">
            Wybierz, które ikony mają być widoczne na pasku narzędzi powyżej listy zamówień.
            Ikony zaznaczania i usuwania są zawsze widoczne.
          </p>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-surface-hover/30 rounded-lg">
              <div>
                <span className="text-text-primary text-sm">Zmiana statusu</span>
                <p className="text-xs text-text-muted">Ikonka zmiany statusu zamówień</p>
              </div>
              <Toggle
                checked={toolbarIcons.showStatusChange}
                onChange={(e) => handleSaveToolbarSettings('showStatusChange', e.target.checked)}
                disabled={savingToolbar}
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-surface-hover/30 rounded-lg">
              <div>
                <span className="text-text-primary text-sm">Listy przewozowe</span>
                <p className="text-xs text-text-muted">Nadawanie i pobieranie listów przewozowych</p>
              </div>
              <Toggle
                checked={toolbarIcons.showLabels}
                onChange={(e) => handleSaveToolbarSettings('showLabels', e.target.checked)}
                disabled={savingToolbar}
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-surface-hover/30 rounded-lg">
              <div>
                <span className="text-text-primary text-sm">Faktury</span>
                <p className="text-xs text-text-muted">Wystawianie i pobieranie faktur</p>
              </div>
              <Toggle
                checked={toolbarIcons.showInvoices}
                onChange={(e) => handleSaveToolbarSettings('showInvoices', e.target.checked)}
                disabled={savingToolbar}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Store Order Section */}
      <div className="card">
        <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
          <Store className="w-5 h-5 text-accent-primary" />
          Kolejność sklepów
        </h3>
        <p className="text-sm text-text-secondary mb-4">
          Ustaw kolejność wyświetlania sklepów w dropdown obok wyszukiwarki zamówień.
          Użyj strzałek, aby zmienić pozycję sklepu.
        </p>

        {orderedStores.length === 0 ? (
          <EmptyState
            icon={Store}
            title="Brak sklepów do uporządkowania"
            size="sm"
          />
        ) : (
          <div className="space-y-2">
            {orderedStores.map((store, index) => (
              <div
                key={store.id}
                className="flex items-center gap-3 p-3 bg-surface-hover/30 rounded-xl group hover:bg-surface-hover/50 transition-colors"
              >
                <GripVertical className="w-4 h-4 text-text-muted" />
                <span className="w-8 h-8 flex items-center justify-center bg-surface-muted rounded-lg text-sm font-medium text-text-secondary">
                  {index + 1}
                </span>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-text-primary font-medium">{store.name}</span>
                    {!store.isActive && (
                      <span className="text-xs px-2 py-0.5 bg-surface-hover rounded text-text-secondary">nieaktywny</span>
                    )}
                  </div>
                  <span className="text-xs text-text-muted">{store.url}</span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => moveStore(index, 'up')}
                    disabled={index === 0}
                    className="p-2 rounded-lg text-text-secondary hover:text-text-primary hover:bg-surface-muted disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                    title="Przenieś w górę"
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => moveStore(index, 'down')}
                    disabled={index === orderedStores.length - 1}
                    className="p-2 rounded-lg text-text-secondary hover:text-text-primary hover:bg-surface-muted disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                    title="Przenieś w dół"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sticky save bar */}
      <div className="sticky bottom-0 z-30 -mx-1 px-1 pt-4">
        <div className="flex items-center gap-3 p-4 rounded-xl bg-surface-raised/95 backdrop-blur-sm border border-border shadow-lg">
          <Button
            onClick={handleSave}
            disabled={orderedStores.length === 0}
            variant="primary"
            size="sm"
            loading={saving}
            leftIcon={<Save />}
          >
            Zapisz ustawienia
          </Button>
          {saveStatus === 'success' && (
            <span className="flex items-center gap-1.5 text-xs text-success animate-fade-in">
              <CheckCircle className="w-3.5 h-3.5" /> Zapisano
            </span>
          )}
          {saveStatus === 'error' && (
            <span className="flex items-center gap-1.5 text-xs text-danger animate-fade-in">
              <AlertCircle className="w-3.5 h-3.5" /> Błąd zapisu
            </span>
          )}
        </div>
      </div>

      {toast && <SettingsToast message={toast.message} type={toast.type} onClose={clearToast} />}
    </div>
  );
}

// ============ ORDER HANDLING SETTINGS ============

// N2 (2026-04-22): customer blacklist config shape.
interface BlacklistConfig {
  enabled: boolean;
  failedDeliveriesConsecutiveThreshold: number;
  failedDeliveriesTotalThreshold: number;
  duplicateOrdersIn24hThreshold: number;
  matchBy: { email: boolean; phone: boolean; address: boolean };
  blockNewOrders: boolean;
  skipInBulkShipment: boolean;
  autoAddOnFailedDeliveries: boolean;
  autoAddOnDuplicateOrders: boolean;
}

const DEFAULT_BLACKLIST_CONFIG: BlacklistConfig = {
  enabled: true,
  failedDeliveriesConsecutiveThreshold: 2,
  failedDeliveriesTotalThreshold: 3,
  duplicateOrdersIn24hThreshold: 3,
  matchBy: { email: true, phone: true, address: false },
  blockNewOrders: true,
  skipInBulkShipment: true,
  autoAddOnFailedDeliveries: true,
  autoAddOnDuplicateOrders: true,
};

interface ResendWorkflowSettingsData {
  defaultResendOrderStatusId: string | null;
  autoInvoiceResend: boolean;
  resendInvoiceSendToKsef: boolean;
  fakturowniaConfigured: boolean;
}

const DEFAULT_RESEND_WORKFLOW_SETTINGS: ResendWorkflowSettingsData = {
  defaultResendOrderStatusId: null,
  autoInvoiceResend: false,
  resendInvoiceSendToKsef: false,
  fakturowniaConfigured: false,
};

function OrderHandlingSettings() {
  const [loading, setLoading] = useState(true);
  const { showError } = useSettingsToast();
  const [automationErrorReporting, setAutomationErrorReporting] = useState<AutomationErrorReportingData>({
    enabled: false,
    emailConfigId: null,
    frequency: 'hourly',
    urgency: 'high',
  });
  const [emailConfigs, setEmailConfigs] = useState<StoreEmailConfig[]>([]);
  const [syncManualOrders, setSyncManualOrders] = useState(false);
  const [manualOrderWooStatus, setManualOrderWooStatus] = useState('processing');
  const [syncSettings, setSyncSettings] = useState<SyncSettings>({
    autoSyncEnabled: true,
    syncIntervalMinutes: 5,
    syncOnStartup: true,
  });
  const [blacklistConfig, setBlacklistConfig] = useState<BlacklistConfig>(DEFAULT_BLACKLIST_CONFIG);
  const [stores, setStores] = useState<StoreBasic[]>([]);
  const [statuses, setStatuses] = useState<StatusOption[]>([]);
  const [resendWorkflowStoreId, setResendWorkflowStoreId] = useState('');
  const [resendWorkflow, setResendWorkflow] = useState<ResendWorkflowSettingsData>(DEFAULT_RESEND_WORKFLOW_SETTINGS);
  const [resendWorkflowLoading, setResendWorkflowLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    const loadData = async () => {
      try {
        const [settingsRes, storesRes, statusesRes, emailConfigsRes] = await Promise.all([
          api.get<{ sync: SyncSettings; syncManualOrdersToWooCommerce: boolean; manualOrderWooStatus: string; customerBlacklist?: BlacklistConfig; automationErrorReporting?: AutomationErrorReportingData }>('/settings'),
          api.get<StoreBasic[]>('/stores'),
          api.get<StatusOption[]>('/statuses'),
          emailApi.getStoreConfigs(),
        ]);
        setAutomationErrorReporting({
          enabled: settingsRes.data.automationErrorReporting?.enabled ?? false,
          emailConfigId: settingsRes.data.automationErrorReporting?.emailConfigId ?? null,
          frequency: settingsRes.data.automationErrorReporting?.frequency ?? 'hourly',
          urgency: settingsRes.data.automationErrorReporting?.urgency ?? 'high',
        });
        setEmailConfigs((emailConfigsRes.data.configs || []).filter(config => config.isActive));
        setSyncSettings(settingsRes.data.sync);
        setSyncManualOrders(settingsRes.data.syncManualOrdersToWooCommerce ?? false);
        setManualOrderWooStatus(settingsRes.data.manualOrderWooStatus ?? '');
        if (settingsRes.data.customerBlacklist) {
          setBlacklistConfig({ ...DEFAULT_BLACKLIST_CONFIG, ...settingsRes.data.customerBlacklist });
        }
        setStores(storesRes.data);
        setStatuses(statusesRes.data);
        setResendWorkflowStoreId(storesRes.data[0]?.id || '');
      } catch (err) {
        console.error('Failed to load data:', err);
        showError('Nie udało się wczytać danych');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    if (!resendWorkflowStoreId) {
      setResendWorkflow(DEFAULT_RESEND_WORKFLOW_SETTINGS);
      return;
    }
    let cancelled = false;
    setResendWorkflowLoading(true);
    api.get<ResendWorkflowSettingsData>(`/settings/resend-workflow/${resendWorkflowStoreId}`)
      .then((response) => {
        if (!cancelled) setResendWorkflow(response.data);
      })
      .catch((error) => {
        console.error('Failed to load resend workflow settings:', error);
        if (!cancelled) showError('Nie udało się wczytać ustawień ponownej wysyłki');
      })
      .finally(() => {
        if (!cancelled) setResendWorkflowLoading(false);
      });
    return () => { cancelled = true; };
  }, [resendWorkflowStoreId, showError]);

  const handleSave = async () => {
    if (automationErrorReporting.enabled && !automationErrorReporting.emailConfigId) {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
      return;
    }

    if (resendWorkflowLoading) return;
    setSaving(true);
    try {
      await Promise.all([
        api.put('/settings/sync', syncSettings),
        api.put('/settings/automation-error-reporting', automationErrorReporting),
        api.put('/settings/sync-manual-orders', { syncManualOrdersToWooCommerce: syncManualOrders, manualOrderWooStatus }),
        api.put('/settings/customer-blacklist', blacklistConfig),
        ...(resendWorkflowStoreId ? [api.put(`/settings/resend-workflow/${resendWorkflowStoreId}`, {
          defaultResendOrderStatusId: resendWorkflow.defaultResendOrderStatusId,
          autoInvoiceResend: resendWorkflow.autoInvoiceResend,
          resendInvoiceSendToKsef: resendWorkflow.autoInvoiceResend && resendWorkflow.resendInvoiceSendToKsef,
        })] : []),
      ]);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-accent-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader icon={ClipboardList} title="Obsługa zamówień" subtitle="Automatyczna synchronizacja danych i przetwarzanie adresów" />

      {/* Sync Settings */}
      <div className="card">
        <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
          <RefreshCw className="w-5 h-5 text-accent-primary" />
          Ustawienia synchronizacji
        </h3>

        <div className="space-y-4">
          {/* Auto Sync Toggle */}
          <div className="flex items-center justify-between p-4 bg-surface-hover/30 rounded-xl">
            <div>
              <h4 className="text-text-primary font-medium">Automatyczna synchronizacja</h4>
              <p className="text-sm text-text-secondary">Włącz automatyczne pobieranie zamówień i aktualizację statusów przesyłek InPost</p>
            </div>
            <Toggle
              checked={syncSettings.autoSyncEnabled}
              onChange={(e) => setSyncSettings(prev => ({ ...prev, autoSyncEnabled: e.target.checked }))}
            />
          </div>

          {/* Global Sync Interval */}
          <div className="p-4 bg-surface-hover/30 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-accent-primary" />
              <label className="text-text-primary font-medium">Interwał synchronizacji</label>
            </div>
            <select
              value={syncSettings.syncIntervalMinutes}
              onChange={(e) => setSyncSettings(prev => ({ ...prev, syncIntervalMinutes: parseInt(e.target.value) }))}
              className="input"
              disabled={!syncSettings.autoSyncEnabled}
            >
              {SYNC_INTERVALS.map(interval => (
                <option key={interval.value} value={interval.value}>{interval.label}</option>
              ))}
            </select>
            <p className="text-xs text-text-muted mt-2">
              Jak często system będzie automatycznie synchronizować zamówienia ze sklepów oraz statusy przesyłek InPost
            </p>
          </div>

          {/* Sync on Startup */}
          <div className="flex items-center justify-between p-4 bg-surface-hover/30 rounded-xl">
            <div>
              <h4 className="text-text-primary font-medium">Synchronizacja przy starcie</h4>
              <p className="text-sm text-text-secondary">Automatycznie synchronizuj dane 30 sekund po uruchomieniu serwera</p>
            </div>
            <Toggle
              checked={syncSettings.syncOnStartup}
              onChange={(e) => setSyncSettings(prev => ({ ...prev, syncOnStartup: e.target.checked }))}
            />
          </div>
        </div>
      </div>

      {/* Manual Orders to WC */}
      <div className="card">
        <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
          <Store className="w-5 h-5 text-accent-primary" />
          Zamówienia ręczne
        </h3>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-surface-hover/30 rounded-xl">
            <div>
              <h4 className="text-text-primary font-medium">Wysyłaj ręczne zamówienia do WooCommerce</h4>
              <p className="text-sm text-text-secondary">
                Po utworzeniu zamówienia ręcznego, automatycznie utwórz odpowiadające zamówienie w WooCommerce.
                Numer zamówienia zostanie pobrany z WooCommerce.
              </p>
            </div>
            <Toggle
              checked={syncManualOrders}
              onChange={(e) => setSyncManualOrders(e.target.checked)}
            />
          </div>

          {syncManualOrders && (
            <div className="p-4 bg-surface-hover/30 rounded-xl">
              <label className="text-text-primary font-medium block mb-2">
                Status zamówienia w WooCommerce
              </label>
              <p className="text-sm text-text-secondary mb-3">
                Wybierz jaki status ma mieć zamówienie utworzone ręcznie w WooCommerce
              </p>
              <select
                value={manualOrderWooStatus}
                onChange={(e) => setManualOrderWooStatus(e.target.value)}
                className="input max-w-xs"
              >
                <option value="">Domyślne mapowanie statusów</option>
                <option value="pending">Oczekujące na płatność (pending)</option>
                <option value="processing">W trakcie realizacji (processing)</option>
                <option value="on-hold">Wstrzymane (on-hold)</option>
                <option value="completed">Zrealizowane (completed)</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Paid resend workflow */}
      <div className="card">
        <h3 className="text-lg font-semibold text-text-primary mb-2 flex items-center gap-2">
          <PackagePlus className="w-5 h-5 text-warning" />
          Ponowna wysyłka po płatności
        </h3>
        <p className="text-sm text-text-muted mb-5">
          Po potwierdzeniu płatności Przelewy24 system tworzy osobne zamówienie, dodaje notatkę z datą i oznacza je jako wymagające ponownego nadania. Oznaczenie znika po utworzeniu listu przewozowego.
        </p>

        {stores.length === 0 ? (
          <EmptyState icon={Store} title="Brak sklepów" description="Dodaj sklep, aby skonfigurować obsługę ponownych wysyłek." />
        ) : (
          <div className={`space-y-4 ${resendWorkflowLoading ? 'opacity-60 pointer-events-none' : ''}`}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label htmlFor="resend-workflow-store" className="block text-sm font-medium text-text-secondary mb-2">Sklep</label>
                <Select
                  id="resend-workflow-store"
                  value={resendWorkflowStoreId}
                  onChange={(event) => setResendWorkflowStoreId(event.target.value)}
                >
                  {stores.map(store => <option key={store.id} value={store.id}>{store.name}</option>)}
                </Select>
              </div>
              <div>
                <label htmlFor="resend-workflow-status" className="block text-sm font-medium text-text-secondary mb-2">Status po opłaceniu</label>
                <StatusTreeSelect
                  id="resend-workflow-status"
                  value={resendWorkflow.defaultResendOrderStatusId || ''}
                  onChange={(value) => setResendWorkflow(prev => ({ ...prev, defaultResendOrderStatusId: value || null }))}
                  statuses={statuses.filter(status => status.storeId === null || status.storeId === resendWorkflowStoreId)}
                  placeholder="Domyślny status"
                />
                <p className="text-xs text-text-muted mt-1">Do tego statusu trafi nowe, opłacone zamówienie ponownej wysyłki.</p>
              </div>
            </div>

            <div className="flex items-center justify-between gap-4 p-4 bg-surface-hover/30 rounded-xl">
              <div>
                <h4 className="text-text-primary font-medium">Wystaw fakturę automatycznie</h4>
                <p className="text-sm text-text-secondary">Faktura zostanie wystawiona dla osobnego zamówienia z opłatą za ponowną wysyłkę i będzie dostępna w Panelu klienta.</p>
              </div>
              <Toggle
                checked={resendWorkflow.autoInvoiceResend}
                disabled={!resendWorkflow.fakturowniaConfigured}
                onChange={(event) => setResendWorkflow(prev => ({ ...prev, autoInvoiceResend: event.target.checked }))}
              />
            </div>

            {!resendWorkflow.fakturowniaConfigured && (
              <div className="flex items-start gap-2 p-3 rounded-lg border border-warning/30 bg-warning/10 text-warning text-sm">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                Najpierw skonfiguruj aktywną integrację Fakturownia.
              </div>
            )}

            {resendWorkflow.autoInvoiceResend && resendWorkflow.fakturowniaConfigured && (
              <div className="flex items-center justify-between gap-4 p-4 bg-surface-hover/30 rounded-xl">
                <div>
                  <h4 className="text-text-primary font-medium">Wyślij fakturę do KSeF</h4>
                  <p className="text-sm text-text-secondary">Używa tych samych danych i przypisania działu co pozostałe faktury sklepu.</p>
                </div>
                <Toggle
                  checked={resendWorkflow.resendInvoiceSendToKsef}
                  onChange={(event) => setResendWorkflow(prev => ({ ...prev, resendInvoiceSendToKsef: event.target.checked }))}
                />
              </div>
            )}
          </div>
        )}
      </div>

      <div className="card p-5">
        <div className="flex items-start gap-4">
          <div className="p-2.5 rounded-xl bg-danger/10 flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-danger" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h3 className="text-sm font-semibold text-text-primary">Raportowanie błędów automatyzacji</h3>
                <p className="text-xs text-text-muted mt-1">
                  Mail trafia na wybraną skrzynkę sklepu, kiedy automatyzacja zakończy się błędem.
                </p>
              </div>
              <button
                onClick={() => setAutomationErrorReporting(prev => ({ ...prev, enabled: !prev.enabled }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                  automationErrorReporting.enabled ? 'bg-accent-primary' : 'bg-surface-muted'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    automationErrorReporting.enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {automationErrorReporting.enabled && (
              <div className="mt-4 grid gap-3 md:grid-cols-3">
                <label className="space-y-1.5">
                  <span className="text-xs text-text-secondary">Skrzynka</span>
                  <Select
                    value={automationErrorReporting.emailConfigId ?? ''}
                    onChange={e => setAutomationErrorReporting(prev => ({
                      ...prev,
                      emailConfigId: e.target.value || null,
                    }))}
                    className="input w-full text-sm"
                  >
                    <option value="">Wybierz skrzynkę</option>
                    {emailConfigs.map(config => (
                      <option key={config.id} value={config.id}>
                        {(config.store?.name || 'Sklep')} - {config.fromEmail}
                      </option>
                    ))}
                  </Select>
                </label>

                <label className="space-y-1.5">
                  <span className="text-xs text-text-secondary">Częstotliwość</span>
                  <Select
                    value={automationErrorReporting.frequency}
                    onChange={e => setAutomationErrorReporting(prev => ({
                      ...prev,
                      frequency: e.target.value as AutomationErrorReportingData['frequency'],
                    }))}
                    className="input w-full text-sm"
                  >
                    <option value="immediate">Każdy błąd</option>
                    <option value="hourly">Najwyżej raz na godzinę</option>
                    <option value="daily">Najwyżej raz dziennie</option>
                  </Select>
                </label>

                <label className="space-y-1.5">
                  <span className="text-xs text-text-secondary">Pilność</span>
                  <Select
                    value={automationErrorReporting.urgency}
                    onChange={e => setAutomationErrorReporting(prev => ({
                      ...prev,
                      urgency: e.target.value as AutomationErrorReportingData['urgency'],
                    }))}
                    className="input w-full text-sm"
                  >
                    <option value="normal">Normalna</option>
                    <option value="high">Pilna</option>
                    <option value="critical">Krytyczna</option>
                  </Select>
                </label>

                {emailConfigs.length === 0 && (
                  <div className="md:col-span-3 rounded-lg border border-warning/20 bg-warning/10 px-3 py-2 text-xs text-warning">
                    Brak aktywnych skrzynek wysyłkowych dla sklepów.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Address merge UI - R3.5 (2026-04-22): przeniesione do Ustawienia -> Walidacja danych */}

      {/* N2 (2026-04-22): Customer blacklist configuration */}
      <div className="card">
        <h3 className="text-lg font-semibold text-text-primary mb-2 flex items-center gap-2">
          <SkullIcon className="w-5 h-5 text-accent-primary" />
          Czarna lista klientów
        </h3>
        <p className="text-sm text-text-muted mb-4">
          Klienci z czarnej listy są pomijani przy masowym nadawaniu paczek, a ich nowe zamówienia mogą być blokowane. Klienta można dodać ręcznie ikoną czaszki na karcie zamówienia albo pozwolić systemowi robić to automatycznie (np. po 2 zwrotach z rzędu).
        </p>

        <div className="space-y-4">
          {/* Master toggle */}
          <label className="flex items-start gap-3 p-3 bg-surface-hover/30 rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={blacklistConfig.enabled}
              onChange={(e) => setBlacklistConfig(prev => ({ ...prev, enabled: e.target.checked }))}
              className="mt-0.5 w-4 h-4 rounded border-border text-accent-primary focus:ring-accent-primary bg-surface-raised"
            />
            <div>
              <p className="text-sm font-medium text-text-primary">Włącz czarną listę</p>
              <p className="text-xs text-text-muted mt-0.5">Globalny wyłącznik. Gdy wyłączony, wszystkie zabezpieczenia są nieaktywne.</p>
            </div>
          </label>

          {blacklistConfig.enabled && (
            <>
              {/* Thresholds */}
              <div className="p-4 bg-surface-hover/20 rounded-lg border border-border-subtle space-y-4">
                <h4 className="text-xs font-semibold text-text-primary uppercase tracking-wider">Progi automatycznego dodawania</h4>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-text-secondary mb-1">Zwroty z rzędu</label>
                    <input
                      type="number"
                      min={1}
                      max={20}
                      value={blacklistConfig.failedDeliveriesConsecutiveThreshold}
                      onChange={(e) => setBlacklistConfig(prev => ({
                        ...prev,
                        failedDeliveriesConsecutiveThreshold: Math.max(1, parseInt(e.target.value) || 2),
                      }))}
                      className="input w-full text-sm"
                    />
                    <p className="text-xs text-text-disabled mt-1">Np. 2 = po 2 zwrotach z rzędu klient na listę.</p>
                  </div>

                  <div>
                    <label className="block text-xs text-text-secondary mb-1">Zwroty ogółem (365 dni)</label>
                    <input
                      type="number"
                      min={1}
                      max={50}
                      value={blacklistConfig.failedDeliveriesTotalThreshold}
                      onChange={(e) => setBlacklistConfig(prev => ({
                        ...prev,
                        failedDeliveriesTotalThreshold: Math.max(1, parseInt(e.target.value) || 3),
                      }))}
                      className="input w-full text-sm"
                    />
                    <p className="text-xs text-text-disabled mt-1">Np. 3 = po 3 zwrotach w ciągu roku.</p>
                  </div>

                  <div>
                    <label className="block text-xs text-text-secondary mb-1">Zamówienia / 24h</label>
                    <input
                      type="number"
                      min={1}
                      max={50}
                      value={blacklistConfig.duplicateOrdersIn24hThreshold}
                      onChange={(e) => setBlacklistConfig(prev => ({
                        ...prev,
                        duplicateOrdersIn24hThreshold: Math.max(1, parseInt(e.target.value) || 3),
                      }))}
                      className="input w-full text-sm"
                    />
                    <p className="text-xs text-text-disabled mt-1">Np. 3 = 3 zamówienia od tego samego klienta w ciągu doby.</p>
                  </div>
                </div>
              </div>

              {/* Matching */}
              <div className="p-4 bg-surface-hover/20 rounded-lg border border-border-subtle">
                <h4 className="text-xs font-semibold text-text-primary uppercase tracking-wider mb-3">Co identyfikuje klienta</h4>
                <p className="text-xs text-text-muted mb-3">Po czym system dopasowuje klienta do wpisu na czarnej liście. Zaznacz wszystkie które mają być sprawdzane.</p>
                <div className="space-y-2">
                  {[
                    { key: 'email' as const, label: 'Adres email', desc: 'Dopasowanie po adresie email (po normalizacji - lowercase).' },
                    { key: 'phone' as const, label: 'Numer telefonu', desc: 'Dopasowanie po numerze (po normalizacji - bez spacji, bez +48).' },
                    { key: 'address' as const, label: 'Adres dostawy', desc: 'Dopasowanie po pełnym adresie (ulica + kod + miasto, po normalizacji).' },
                  ].map(field => (
                    <label key={field.key} className="flex items-start gap-3 p-2 rounded cursor-pointer hover:bg-surface-hover/30">
                      <input
                        type="checkbox"
                        checked={blacklistConfig.matchBy[field.key]}
                        onChange={(e) => setBlacklistConfig(prev => ({
                          ...prev,
                          matchBy: { ...prev.matchBy, [field.key]: e.target.checked },
                        }))}
                        className="mt-0.5 w-4 h-4 rounded border-border text-accent-primary focus:ring-accent-primary bg-surface-raised"
                      />
                      <div>
                        <p className="text-sm text-text-primary">{field.label}</p>
                        <p className="text-xs text-text-muted">{field.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="p-4 bg-surface-hover/20 rounded-lg border border-border-subtle">
                <h4 className="text-xs font-semibold text-text-primary uppercase tracking-wider mb-3">Zachowanie systemu</h4>
                <div className="space-y-2">
                  {[
                    { key: 'autoAddOnFailedDeliveries' as const, label: 'Automatyczne dodawanie po zwrotach', desc: 'Gdy próg "zwroty z rzędu" lub "zwroty ogółem" zostanie przekroczony, klient trafia na listę bez działania operatora.' },
                    { key: 'autoAddOnDuplicateOrders' as const, label: 'Automatyczne dodawanie po duplikatach zamówień', desc: 'Gdy ten sam klient (wg identyfikacji wyżej) złoży więcej zamówień w 24h niż próg, trafia na listę.' },
                    { key: 'skipInBulkShipment' as const, label: 'Pomijaj na czarnej liście przy nadawaniu masowym', desc: 'handleBulkShipment odfiltruje te zamówienia z kolejki. Operator musi nadać je ręcznie.' },
                    { key: 'blockNewOrders' as const, label: 'Blokuj tworzenie nowych zamówień', desc: 'Przy synchronizacji z WooCommerce oraz ręcznym tworzeniu - zamówienie klienta z czarnej listy zostanie oznaczone, a operator musi zdecydować co dalej.' },
                  ].map(action => (
                    <label key={action.key} className="flex items-start gap-3 p-2 rounded cursor-pointer hover:bg-surface-hover/30">
                      <input
                        type="checkbox"
                        checked={blacklistConfig[action.key]}
                        onChange={(e) => setBlacklistConfig(prev => ({ ...prev, [action.key]: e.target.checked }))}
                        className="mt-0.5 w-4 h-4 rounded border-border text-accent-primary focus:ring-accent-primary bg-surface-raised"
                      />
                      <div>
                        <p className="text-sm text-text-primary">{action.label}</p>
                        <p className="text-xs text-text-muted">{action.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Link do listy */}
              <div className="p-3 bg-accent-primary/5 border border-accent-primary/20 rounded-lg flex items-center justify-between">
                <p className="text-sm text-text-secondary">Zarządzaj wpisami na liście (przegląd, dodaj ręcznie, usuń):</p>
                <Link
                  to="/admin/settings/blacklist"
                  className={buttonVariants({ variant: 'secondary', size: 'sm' })}
                >
                  Otwórz listę
                </Link>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Sticky save bar */}
      <div className="sticky bottom-0 z-30 -mx-1 px-1 pt-4">
        <div className="flex items-center gap-3 p-4 rounded-xl bg-surface-raised/95 backdrop-blur-sm border border-border shadow-lg">
          <Button onClick={handleSave} disabled={resendWorkflowLoading} variant="primary" size="sm" loading={saving} leftIcon={<Save />}>
            Zapisz ustawienia
          </Button>
          {saveStatus === 'success' && (
            <span className="flex items-center gap-1.5 text-xs text-success animate-fade-in">
              <CheckCircle className="w-3.5 h-3.5" /> Zapisano
            </span>
          )}
          {saveStatus === 'error' && (
            <span className="flex items-center gap-1.5 text-xs text-danger animate-fade-in">
              <AlertCircle className="w-3.5 h-3.5" /> Błąd zapisu
            </span>
          )}
        </div>
      </div>
    </div>
  );
}




interface StatusOption {
  id: string;
  name: string;
  color: string;
  parentId: string | null;
  storeId: string | null;
  store?: { id: string; name: string } | null;
}

// ─── Portal Settings (includes resend settings) ─────────────────────────────
interface PortalSettingsData {
  portalEnabled: boolean;
  portalComplaintsEnabled: boolean;
  portalInvoicesEnabled: boolean;
  portalResendEnabled: boolean;
  portalAddressEditEnabled: boolean;
}

interface ResendSettingsData {
  resendAmount: number | null;
  linkExpirationHours: number;
  brandingLogoUrl: string | null;
  brandingBackgroundColor: string;
  brandingAccentColor: string;
  brandingFontColor: string;
  brandingButtonFontColor: string;
  brandingCardColor: string;
  brandingSeparatorColor: string;
  brandingButtonColor: string;
  brandingButtonHoverColor: string;
  brandingLogoMaxWidth: number;
  brandingLogoMaxWidthMobile: number;
  syncResendToWooCommerce: boolean;
}

const EXPIRATION_OPTIONS = [
  { value: 12, label: '12 godzin' },
  { value: 24, label: '24 godziny' },
  { value: 48, label: '48 godzin' },
  { value: 72, label: '72 godziny' },
  { value: 96, label: '96 godzin' },
  { value: 168, label: '7 dni' },
];

const THEME_PRESETS = {
  light: {
    brandingBackgroundColor: '#f5f5f7',
    brandingAccentColor: '#2563eb',
    brandingFontColor: '#1f2937',
    brandingButtonFontColor: '#ffffff',
    brandingCardColor: '#ffffff',
    brandingSeparatorColor: '#d7dde6',
    brandingButtonColor: '#f1f5f9',
    brandingButtonHoverColor: '#e2e8f0',
  },
  dark: {
    brandingBackgroundColor: '#161722',
    brandingAccentColor: '#4da3ff',
    brandingFontColor: '#f8fafc',
    brandingButtonFontColor: '#ffffff',
    brandingCardColor: '#20222f',
    brandingSeparatorColor: '#35384a',
    brandingButtonColor: '#292c3d',
    brandingButtonHoverColor: '#34384d',
  },
};

function detectTheme(settings: ResendSettingsData): string {
  const bg = settings.brandingBackgroundColor?.toLowerCase();
  if (bg === THEME_PRESETS.light.brandingBackgroundColor) return 'light';
  if (bg === THEME_PRESETS.dark.brandingBackgroundColor) return 'dark';
  return '';
}

function PortalSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const { showError } = useSettingsToast();
  const [stores, setStores] = useState<StoreBasic[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<string>('');
  const [selectedTheme, setSelectedTheme] = useState<string>('');
  const [portalSettings, setPortalSettings] = useState<PortalSettingsData>({
    portalEnabled: true,
    portalComplaintsEnabled: true,
    portalInvoicesEnabled: true,
    portalResendEnabled: true,
    portalAddressEditEnabled: true,
  });
  const [resendSettings, setResendSettings] = useState<ResendSettingsData>({
    resendAmount: null,
    linkExpirationHours: 48,
    brandingLogoUrl: null,
    brandingBackgroundColor: '#161722',
    brandingAccentColor: '#4da3ff',
    brandingFontColor: '#f8fafc',
    brandingButtonFontColor: '#ffffff',
    brandingCardColor: '#20222f',
    brandingSeparatorColor: '#35384a',
    brandingButtonColor: '#292c3d',
    brandingButtonHoverColor: '#34384d',
    brandingLogoMaxWidth: 200,
    brandingLogoMaxWidthMobile: 140,
    syncResendToWooCommerce: false,
  });
  const [logoError, setLogoError] = useState(false);
  const [previewHoveredAction, setPreviewHoveredAction] = useState<string | null>(null);

  useEffect(() => {
    const loadInitial = async () => {
      try {
        const storesRes = await api.get<StoreBasic[]>('/stores');
        setStores(storesRes.data);
        if (storesRes.data.length > 0) {
          setSelectedStoreId(storesRes.data[0].id);
        }
      } catch {
        showError('Nie udało się wczytać danych');
      } finally {
        setLoading(false);
      }
    };
    loadInitial();
  }, []);

  // Load both portal and resend settings when store changes
  useEffect(() => {
    if (!selectedStoreId) return;
    const loadSettings = async () => {
      try {
        const [portalRes, resendRes] = await Promise.all([
          api.get<PortalSettingsData>(`/settings/portal/${selectedStoreId}`),
          api.get<ResendSettingsData>(`/settings/resend/${selectedStoreId}`),
        ]);
        setPortalSettings(portalRes.data);
        setResendSettings(resendRes.data);
        setSelectedTheme(detectTheme(resendRes.data));
        setLogoError(false);
      } catch (err) {
        console.error('Failed to load settings:', err);
      }
    };
    loadSettings();
  }, [selectedStoreId]);

  const handleSubmit = async () => {
    if (!selectedStoreId) return;
    setSaving(true);
    const errors: string[] = [];
    try {
      // Save sequentially to avoid race condition - both endpoints read/write Store.settings
      try {
        await api.put(`/settings/portal/${selectedStoreId}`, portalSettings);
      } catch (err) {
        console.error('Failed to save portal settings:', err);
        errors.push('ustawień panelu');
      }
      try {
        await api.put(`/settings/resend/${selectedStoreId}`, resendSettings);
      } catch (err) {
        console.error('Failed to save resend/branding settings:', err);
        errors.push('brandingu i ponownej wysyłki');
      }
      if (errors.length === 0) {
        setSaveStatus('success');
      } else {
        setSaveStatus('error');
      }
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } finally {
      setSaving(false);
    }
  };

  const toggleItems: Array<{ key: keyof PortalSettingsData; label: string; description: string }> = [
    {
      key: 'portalEnabled',
      label: 'Panel klienta włączony',
      description: 'Główny przełącznik - wyłączenie dezaktywuje cały panel klienta dla tego sklepu',
    },
    {
      key: 'portalAddressEditEnabled',
      label: 'Edycja adresu dostawy',
      description: 'Pozwól klientom na zmianę adresu dostawy z poziomu panelu',
    },
    {
      key: 'portalResendEnabled',
      label: 'Ponowna wysyłka',
      description: 'Pozwól klientom na inicjowanie ponownej wysyłki z panelu',
    },
    {
      key: 'portalInvoicesEnabled',
      label: 'Pobieranie faktur',
      description: 'Pozwól klientom na przeglądanie i pobieranie faktur',
    },
    {
      key: 'portalComplaintsEnabled',
      label: 'Reklamacje',
      description: 'Pozwól klientom na składanie reklamacji i zgłaszanie zwrotów',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-accent-primary" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <PageHeader icon={Users} title="Panel klienta" subtitle="Konfiguracja funkcji panelu klienta i ponownej wysyłki" />

      {/* Store selector */}
      <div className="card mb-6">
        <label className="block text-sm font-medium text-text-secondary mb-2">Sklep</label>
        <select
          value={selectedStoreId}
          onChange={(e) => setSelectedStoreId(e.target.value)}
          className="input w-full max-w-md"
        >
          {stores.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
      </div>

      {selectedStoreId && (
        <div className="space-y-6">
          {/* Available features */}
          <div className="card">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Dostępne funkcje</h3>
            <p className="text-sm text-text-secondary mb-6">
              Wybierz, które funkcje mają być dostępne dla klientów w panelu.
            </p>
            <div className="space-y-4">
              {toggleItems.map((item) => (
                <div
                  key={item.key}
                  className={`flex items-center justify-between p-4 rounded-lg border transition-all ${
                    portalSettings[item.key]
                      ? 'border-accent-primary/30 bg-accent-primary/5'
                      : 'border-border-subtle bg-surface-raised'
                  } ${item.key !== 'portalEnabled' && !portalSettings.portalEnabled ? 'opacity-40 pointer-events-none' : ''}`}
                >
                  <div className="flex-1 mr-4">
                    <div className="text-sm font-medium text-text-primary">{item.label}</div>
                    <div className="text-xs text-text-muted mt-0.5">{item.description}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPortalSettings({ ...portalSettings, [item.key]: !portalSettings[item.key] })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ${
                      portalSettings[item.key] ? 'bg-accent-primary' : 'bg-surface-muted'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        portalSettings[item.key] ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Resend cost */}
          <div className="card">
            <h3 className="text-lg font-semibold text-text-primary mb-2 flex items-center gap-2">
              <Send className="w-5 h-5 text-warning" />
              Ponowna wysyłka
            </h3>
            <p className="text-sm text-text-secondary mb-4">Konfiguracja kosztu i linku płatności ponownej wysyłki</p>
            <div className="space-y-4">
              <div>
                <label htmlFor="resendAmount" className="block text-sm font-medium text-text-secondary mb-2">
                  Kwota ponownej wysyłki
                </label>
                <div className="relative w-full max-w-md">
                  <input
                    id="resendAmount"
                    type="number"
                    min="0.01"
                    max="100000"
                    step="0.01"
                    inputMode="decimal"
                    value={resendSettings.resendAmount ?? ''}
                    onChange={(e) => setResendSettings({
                      ...resendSettings,
                      resendAmount: e.target.value === '' ? null : Number(e.target.value),
                    })}
                    placeholder="np. 14.00"
                    className="input w-full pr-14"
                    aria-describedby="resend-amount-help"
                  />
                  <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm text-text-muted">PLN</span>
                </div>
                <p id="resend-amount-help" className="text-xs text-text-muted mt-1">
                  Tę kwotę pobierze Przelewy24. Opłata zostanie dopisana do osobnego zamówienia ponownej wysyłki bez ruchu magazynowego.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Ważność linku
                </label>
                <select
                  value={resendSettings.linkExpirationHours}
                  onChange={(e) => setResendSettings({ ...resendSettings, linkExpirationHours: parseInt(e.target.value) })}
                  className="input w-full max-w-md"
                >
                  {EXPIRATION_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="syncResendToWooCommerce"
                  checked={resendSettings.syncResendToWooCommerce}
                  onChange={(e) => setResendSettings({ ...resendSettings, syncResendToWooCommerce: e.target.checked })}
                  className="w-4 h-4 rounded border-border bg-surface-hover text-accent-primary focus:ring-accent-primary/50"
                />
                <label htmlFor="syncResendToWooCommerce" className="text-sm text-text-secondary">
                  Synchronizuj zamówienia ponownej wysyłki do WooCommerce
                </label>
              </div>
            </div>
          </div>

          {/* Branding */}
          <div className="card">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Branding panelu klienta</h3>
            <p className="text-sm text-text-secondary mb-4">
              Jeden zestaw kolorów obsługuje panel klienta, formularze i stronę płatności ponownej wysyłki.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">URL logo</label>
                <input
                  type="text"
                  value={resendSettings.brandingLogoUrl || ''}
                  onChange={(e) => { setResendSettings({ ...resendSettings, brandingLogoUrl: e.target.value || null }); setLogoError(false); }}
                  placeholder="https://example.com/logo.png"
                  className="input w-full max-w-md"
                />
              </div>

              <div className="flex gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">Maks. szerokość logo (desktop)</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="60"
                      max="400"
                      value={resendSettings.brandingLogoMaxWidth ?? 200}
                      onChange={(e) => setResendSettings({ ...resendSettings, brandingLogoMaxWidth: parseInt(e.target.value) || 200 })}
                      className="input w-24"
                    />
                    <span className="text-sm text-text-muted">px</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">Maks. szerokość logo (mobile)</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="40"
                      max="300"
                      value={resendSettings.brandingLogoMaxWidthMobile ?? 140}
                      onChange={(e) => setResendSettings({ ...resendSettings, brandingLogoMaxWidthMobile: parseInt(e.target.value) || 140 })}
                      className="input w-24"
                    />
                    <span className="text-sm text-text-muted">px</span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">Motyw kolorystyczny</label>
                <select
                  value={selectedTheme}
                  onChange={(e) => {
                    const theme = e.target.value as 'light' | 'dark' | '';
                    setSelectedTheme(theme);
                    if (!theme) return;
                    setResendSettings(prev => ({ ...prev, ...THEME_PRESETS[theme] }));
                  }}
                  className="input w-full max-w-xs"
                >
                  <option value="">Wybierz motyw...</option>
                  <option value="light">Jasny</option>
                  <option value="dark">Ciemny</option>
                </select>
                <p className="text-xs text-text-muted mt-1">Zmiana motywu resetuje wszystkie kolory do wartości domyślnych</p>
              </div>

              <div className="flex flex-wrap gap-3">
                {([
                  { key: 'brandingBackgroundColor' as const, label: 'Tło' },
                  { key: 'brandingAccentColor' as const, label: 'Akcent' },
                  { key: 'brandingFontColor' as const, label: 'Tekst' },
                  { key: 'brandingButtonFontColor' as const, label: 'Tekst przycisku' },
                  { key: 'brandingCardColor' as const, label: 'Tło kart' },
                  { key: 'brandingSeparatorColor' as const, label: 'Separatory' },
                  { key: 'brandingButtonColor' as const, label: 'Tło przycisku' },
                  { key: 'brandingButtonHoverColor' as const, label: 'Tło przycisku - hover' },
                ] as const).map(({ key, label }) => {
                  // Ensure hex value for the picker (handle legacy rgba values)
                  const raw = resendSettings[key] || '#000000';
                  let hexVal = raw;
                  if (raw.startsWith('rgba') || raw.startsWith('rgb')) {
                    const m = raw.match(/(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
                    if (m) {
                      hexVal = '#' + [m[1], m[2], m[3]].map(c => parseInt(c).toString(16).padStart(2, '0')).join('');
                    }
                  }
                  return (
                    <div key={key} className="flex flex-col items-center gap-1">
                      <input
                        type="color"
                        value={hexVal}
                        onChange={(e) => setResendSettings({ ...resendSettings, [key]: e.target.value })}
                        className="w-10 h-10 rounded border border-border bg-transparent cursor-pointer"
                      />
                      <span className="text-xxs text-text-muted text-center leading-tight">{label}</span>
                    </div>
                  );
                })}
              </div>

              {/* Preview - portal orders list */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">Podgląd panelu klienta</label>
                <div
                  className="rounded-xl overflow-hidden max-w-sm border border-border-subtle"
                  style={{ backgroundColor: resendSettings.brandingBackgroundColor }}
                >
                  {/* Mini header */}
                  <div className="px-5 pt-5 pb-3 flex flex-col items-center gap-2">
                    {resendSettings.brandingLogoUrl && !logoError ? (
                      <img
                        src={resendSettings.brandingLogoUrl}
                        alt="Logo"
                        className="max-h-8 max-w-[140px] object-contain"
                        onError={() => setLogoError(true)}
                      />
                    ) : (
                      <div className="h-8 w-24 rounded bg-surface-hover/60 flex items-center justify-center text-xxs text-text-secondary">
                        {logoError ? 'Błąd' : 'Logo'}
                      </div>
                    )}
                    <p className="text-xs" style={{ color: resendSettings.brandingFontColor, opacity: 0.5 }}>Witaj, Kliencie</p>
                  </div>

                  {/* Mini orders */}
                  <div className="px-5 pb-5 space-y-0">
                    {/* Order 1 */}
                    <div className="flex flex-col items-center">
                      <div
                        className="w-5 h-5 rounded-full flex items-center justify-center text-xxs font-bold"
                        style={{ backgroundColor: `${resendSettings.brandingAccentColor}22`, color: resendSettings.brandingAccentColor }}
                      >1</div>
                      <p className="text-xxs mt-0.5 mb-1" style={{ color: resendSettings.brandingFontColor, opacity: 0.45 }}>12 lutego 2026</p>
                      <div
                        className="w-px h-2 mb-1"
                        style={{ backgroundImage: `repeating-linear-gradient(to bottom, ${resendSettings.brandingSeparatorColor} 0px, ${resendSettings.brandingSeparatorColor} 2px, transparent 2px, transparent 5px)` }}
                      />
                    </div>
                    <div
                      className="rounded-lg p-3 mb-2"
                      style={{ backgroundColor: resendSettings.brandingCardColor, border: `1px solid ${resendSettings.brandingSeparatorColor}` }}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold" style={{ color: resendSettings.brandingFontColor }}>#1052</span>
                        <span className="text-xxs px-1.5 py-0.5 rounded-full font-medium" style={{ backgroundColor: '#22c55e', color: resendSettings.brandingButtonFontColor }}>Wysłane</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xxs" style={{ color: resendSettings.brandingFontColor, opacity: 0.4 }}>InPost — TRACK123</span>
                        <span className="text-xxs font-medium" style={{ color: resendSettings.brandingFontColor, opacity: 0.7 }}>149,99 PLN</span>
                      </div>
                    </div>

                    {/* Order 2 */}
                    <div className="flex flex-col items-center">
                      <div
                        className="w-px h-2"
                        style={{ backgroundImage: `repeating-linear-gradient(to bottom, ${resendSettings.brandingSeparatorColor} 0px, ${resendSettings.brandingSeparatorColor} 2px, transparent 2px, transparent 5px)` }}
                      />
                      <div
                        className="w-5 h-5 rounded-full flex items-center justify-center text-xxs font-bold"
                        style={{ backgroundColor: `${resendSettings.brandingAccentColor}22`, color: resendSettings.brandingAccentColor }}
                      >2</div>
                      <p className="text-xxs mt-0.5 mb-1" style={{ color: resendSettings.brandingFontColor, opacity: 0.45 }}>5 lutego 2026</p>
                      <div
                        className="w-px h-2 mb-1"
                        style={{ backgroundImage: `repeating-linear-gradient(to bottom, ${resendSettings.brandingSeparatorColor} 0px, ${resendSettings.brandingSeparatorColor} 2px, transparent 2px, transparent 5px)` }}
                      />
                    </div>
                    <div
                      className="rounded-lg p-3"
                      style={{ backgroundColor: resendSettings.brandingCardColor, border: `1px solid ${resendSettings.brandingSeparatorColor}` }}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold" style={{ color: resendSettings.brandingFontColor }}>#1048</span>
                        <span className="text-xxs px-1.5 py-0.5 rounded-full font-medium" style={{ backgroundColor: '#f59e0b', color: resendSettings.brandingButtonFontColor }}>W realizacji</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xxs" style={{ color: resendSettings.brandingFontColor, opacity: 0.4 }}>DPD — 7894561230</span>
                        <span className="text-xxs font-medium" style={{ color: resendSettings.brandingFontColor, opacity: 0.7 }}>89,00 PLN</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 pt-3">
                      {[
                        { id: 'address', icon: MapPin, label: 'Zmień dane dostawy' },
                        { id: 'resend', icon: RotateCcw, label: 'Ponowna wysyłka' },
                        { id: 'invoice', icon: FileText, label: 'Pobierz fakturę' },
                        { id: 'complaint', icon: AlertTriangle, label: 'Reklamacje i zwroty' },
                      ].map((action) => {
                        const ActionIcon = action.icon;
                        const isHovered = previewHoveredAction === action.id;
                        return (
                          <button
                            key={action.id}
                            type="button"
                            onMouseEnter={() => setPreviewHoveredAction(action.id)}
                            onMouseLeave={() => setPreviewHoveredAction(null)}
                            onFocus={() => setPreviewHoveredAction(action.id)}
                            onBlur={() => setPreviewHoveredAction(null)}
                            className="flex min-h-14 items-start gap-2 rounded-lg border p-2 text-left transition-colors"
                            style={{
                              backgroundColor: isHovered
                                ? resendSettings.brandingButtonHoverColor
                                : resendSettings.brandingButtonColor,
                              borderColor: isHovered
                                ? resendSettings.brandingAccentColor
                                : resendSettings.brandingSeparatorColor,
                              color: resendSettings.brandingFontColor,
                            }}
                          >
                            <ActionIcon className="h-3.5 w-3.5 shrink-0" style={{ color: resendSettings.brandingAccentColor }} />
                            <span className="text-[9px] font-medium leading-tight">{action.label}</span>
                          </button>
                        );
                      })}
                    </div>
                    <p className="pt-1 text-center text-[9px]" style={{ color: resendSettings.brandingFontColor, opacity: 0.5 }}>
                      Najedź na przycisk, aby sprawdzić kolor hover.
                    </p>
                  </div>

                  {/* Mini footer button */}
                  <div className="px-5 pb-4">
                    <button
                      type="button"
                      className="w-full px-4 py-2 rounded-lg text-xxs font-semibold"
                      style={{ backgroundColor: resendSettings.brandingAccentColor, color: resendSettings.brandingButtonFontColor }}
                    >
                      Reklamacje i zwroty
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sticky save bar */}
          <div className="sticky bottom-0 z-30 -mx-1 px-1 pt-4">
            <div className="flex items-center gap-3 p-4 rounded-xl bg-surface-raised/95 backdrop-blur-sm border border-border shadow-lg">
              <Button onClick={handleSubmit} disabled={!selectedStoreId} variant="primary" size="sm" loading={saving} leftIcon={<Save />}>
                Zapisz ustawienia
              </Button>
              {saveStatus === 'success' && (
                <span className="flex items-center gap-1.5 text-xs text-success animate-fade-in">
                  <CheckCircle className="w-3.5 h-3.5" /> Zapisano
                </span>
              )}
              {saveStatus === 'error' && (
                <span className="flex items-center gap-1.5 text-xs text-danger animate-fade-in">
                  <AlertCircle className="w-3.5 h-3.5" /> Błąd zapisu
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Returns Settings ─────────────────────────────────────────

interface ReturnsSettingsData {
  autoRefundEnabled: boolean;
  emailNotificationsEnabled: boolean;
  emailStatusTransitions: string[];
}

function ReturnsSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const { showError } = useSettingsToast();
  const [stores, setStores] = useState<StoreBasic[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<string>('');
  const [settings, setSettings] = useState<ReturnsSettingsData>({
    autoRefundEnabled: false,
    emailNotificationsEnabled: false,
    emailStatusTransitions: ['ACCEPTED', 'REJECTED', 'RESOLVED'],
  });
  const [hasActiveSmtp, setHasActiveSmtp] = useState(false);

  useEffect(() => {
    const loadInitial = async () => {
      try {
        const storesRes = await api.get<StoreBasic[]>('/stores');
        setStores(storesRes.data);
        if (storesRes.data.length > 0) {
          setSelectedStoreId(storesRes.data[0].id);
        }
      } catch {
        showError('Nie udało się wczytać danych');
      } finally {
        setLoading(false);
      }
    };
    loadInitial();
  }, []);

  useEffect(() => {
    if (!selectedStoreId) return;
    const loadSettings = async () => {
      try {
        const [returnsRes, emailRes] = await Promise.all([
          api.get<ReturnsSettingsData>(`/settings/returns/${selectedStoreId}`),
          api.get<{ configs: Array<{ isActive: boolean; storeId: string }> }>('/email/store-configs'),
        ]);
        setSettings(returnsRes.data);
        // Check if any active SMTP config exists for this store
        const storeConfigs = emailRes.data.configs.filter(
          (c: { storeId: string; isActive: boolean }) => c.storeId === selectedStoreId && c.isActive
        );
        setHasActiveSmtp(storeConfigs.length > 0);
      } catch (err) {
        console.error('Failed to load returns settings:', err);
      }
    };
    loadSettings();
  }, [selectedStoreId]);

  const handleSubmit = async () => {
    if (!selectedStoreId) return;
    setSaving(true);
    try {
      await api.put(`/settings/returns/${selectedStoreId}`, settings);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } finally {
      setSaving(false);
    }
  };

  const toggleTransition = (status: string) => {
    setSettings(prev => ({
      ...prev,
      emailStatusTransitions: prev.emailStatusTransitions.includes(status)
        ? prev.emailStatusTransitions.filter(s => s !== status)
        : [...prev.emailStatusTransitions, status],
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-accent-primary" />
      </div>
    );
  }

  const statusOptions = [
    { value: 'ACCEPTED', label: 'Zaakceptowane' },
    { value: 'REJECTED', label: 'Odrzucone' },
    { value: 'RESOLVED', label: 'Rozwiązane' },
  ];

  return (
    <>
      <div className="animate-fade-in">
        <PageHeader icon={RotateCcw} title="Reklamacje" subtitle="Konfiguracja automatycznych zwrotów i powiadomień email" />

        {/* Store selector */}
        {stores.length > 1 && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-text-secondary mb-2">Sklep</label>
            <select
              value={selectedStoreId}
              onChange={e => setSelectedStoreId(e.target.value)}
              className="input w-full max-w-sm"
            >
              {stores.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
        )}

        <div className="space-y-6">
          {/* Auto refund */}
          <div className="card p-5">
            <div className="flex items-start gap-4">
              <div className="p-2.5 rounded-xl bg-accent-info/10 flex-shrink-0">
                <CreditCard className="w-5 h-5 text-accent-info" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-text-primary">Zwrot środków (Przelewy24)</h3>
                    <p className="text-xs text-text-muted mt-1">
                      Włącz przycisk zwrotu środków w panelu zarządzania zgłoszeniami.
                      Po kliknięciu przycisku środki zostaną zwrócone przez Przelewy24.
                    </p>
                  </div>
                  <button
                    onClick={() => setSettings(prev => ({ ...prev, autoRefundEnabled: !prev.autoRefundEnabled }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ml-4 ${
                      settings.autoRefundEnabled ? 'bg-accent-primary' : 'bg-surface-muted'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.autoRefundEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Email notifications */}
          <div className="card p-5">
            <div className="flex items-start gap-4">
              <div className="p-2.5 rounded-xl bg-accent-secondary/10 flex-shrink-0">
                <Mail className="w-5 h-5 text-accent-secondary" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-text-primary">Powiadomienia email przy zmianie statusu</h3>
                    <p className="text-xs text-text-muted mt-1">
                      Klient otrzyma automatyczny email po zmianie statusu zgłoszenia.
                    </p>
                  </div>
                  <button
                    onClick={() => setSettings(prev => ({ ...prev, emailNotificationsEnabled: !prev.emailNotificationsEnabled }))}
                    disabled={!hasActiveSmtp}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ml-4 ${
                      !hasActiveSmtp ? 'opacity-40 cursor-not-allowed' :
                      settings.emailNotificationsEnabled ? 'bg-accent-primary' : 'bg-surface-muted'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.emailNotificationsEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {!hasActiveSmtp && (
                  <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-warning/10 border border-warning/20 rounded-lg">
                    <AlertCircle className="w-4 h-4 text-warning flex-shrink-0" />
                    <p className="text-xs text-warning">
                      Wymagana aktywna integracja email (SMTP). Skonfiguruj ją w{' '}
                      <Link to="/integrations" className="underline hover:no-underline">Integracje → Email</Link>.
                    </p>
                  </div>
                )}

                {settings.emailNotificationsEnabled && hasActiveSmtp && (
                  <div className="mt-4 pl-0">
                    <p className="text-xs text-text-secondary mb-2">Wyślij email, gdy status zmieni się na:</p>
                    <div className="flex flex-wrap gap-2">
                      {statusOptions.map(opt => (
                        <label
                          key={opt.value}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg cursor-pointer transition-colors text-sm ${
                            settings.emailStatusTransitions.includes(opt.value)
                              ? 'bg-accent-primary/10 text-accent-primary border border-accent-primary/30'
                              : 'bg-surface-hover text-text-secondary border border-border hover:border-border-subtle'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={settings.emailStatusTransitions.includes(opt.value)}
                            onChange={() => toggleTransition(opt.value)}
                            className="sr-only"
                          />
                          {settings.emailStatusTransitions.includes(opt.value) ? (
                            <CheckCircle className="w-3.5 h-3.5" />
                          ) : (
                            <div className="w-3.5 h-3.5 rounded-full border border-border" />
                          )}
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sticky save bar */}
          <div className="sticky bottom-0 z-30 -mx-1 px-1 pt-4">
            <div className="flex items-center gap-3 p-4 rounded-xl bg-surface-raised/95 backdrop-blur-sm border border-border shadow-lg">
              <Button onClick={handleSubmit} variant="primary" size="sm" loading={saving} leftIcon={<Save />}>
                Zapisz ustawienia
              </Button>
              {saveStatus === 'success' && (
                <span className="flex items-center gap-1.5 text-xs text-success animate-fade-in">
                  <CheckCircle className="w-3.5 h-3.5" /> Zapisano
                </span>
              )}
              {saveStatus === 'error' && (
                <span className="flex items-center gap-1.5 text-xs text-danger animate-fade-in">
                  <AlertCircle className="w-3.5 h-3.5" /> Błąd zapisu
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Validation Settings (pkt 5 + 8, 2026-04-22) ──────────────
// Przeniesione z CouriersPage → tu, bo integracje kurierskie odpowiadają tylko
// za integrację; wszystkie ustawienia operatorskie lądują w Ustawieniach.

interface ShipmentValidationData {
  warnOnDuplicateLabels: boolean;
  duplicateLabelThreshold: number;
  emailTypoDetection: boolean;
  emailTldValidation: boolean;
  allowedEmailTlds: string[];
  phoneSmartValidation: boolean;
}

interface AutomationErrorReportingData {
  enabled: boolean;
  emailConfigId: string | null;
  frequency: 'immediate' | 'hourly' | 'daily';
  urgency: 'normal' | 'high' | 'critical';
}

// Default TLD list — mirror backendu (DEFAULT_ALLOWED_EMAIL_TLDS w shipment-validation.service.ts).
const DEFAULT_EMAIL_TLDS = [
  'pl', 'com.pl', 'net.pl', 'org.pl',
  'com', 'org', 'net', 'info', 'biz', 'co', 'io',
  'eu',
  'de', 'fr', 'it', 'es', 'nl', 'se', 'be', 'ie', 'at', 'dk',
  'cz', 'sk', 'hu',
  'shop', 'store', 'online', 'app',
];

// Default adress algorithms keys - mirror backend DEFAULT_ALGORITHMS.
const DEFAULT_ADDRESS_ALGORITHMS: Record<string, boolean> = {
  autoSpace: true,
  normalizeSlashSpaces: true,
  removeStrayPunctuation: true,
  deduplicateSlash: true,
  deduplicateNumbers: true,
  deduplicateFormat: true,
  removeCity: true,
  removePostcode: true,
  removeDescriptive: true,
  removeTrailingDot: true,
  normalizeMNotation: true,
  addApartmentSlash: true,
  stripLetterSlash: true,
  stripTrailingSlash: true,
  dotToSlash: true,
};

function ValidationSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [settings, setSettings] = useState<ShipmentValidationData>({
    warnOnDuplicateLabels: true,
    duplicateLabelThreshold: 5,
    emailTypoDetection: true,
    emailTldValidation: true,
    allowedEmailTlds: [],
    phoneSmartValidation: true,
  });
  const [tldText, setTldText] = useState('');
  // R3.5 (2026-04-22): łączenie adresów z WooCommerce przeniesione tu z "Obsługi zamówień".
  const [addressMergeMode, setAddressMergeMode] = useState<'none' | 'always' | 'smart'>('none');
  const [addressAlgorithms, setAddressAlgorithms] = useState<Record<string, boolean>>(DEFAULT_ADDRESS_ALGORITHMS);
  const [algorithmsExpanded, setAlgorithmsExpanded] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get<{
            shipmentValidation: ShipmentValidationData;
            addressMergeMode?: string;
            addressAlgorithms?: Record<string, boolean> | null;
          }>('/settings');
        const incoming = res.data.shipmentValidation;
        const tlds = incoming.allowedEmailTlds.length > 0 ? incoming.allowedEmailTlds : DEFAULT_EMAIL_TLDS;
        setSettings({ ...incoming, allowedEmailTlds: tlds });
        setTldText(tlds.join(', '));
        setAddressMergeMode((res.data.addressMergeMode as 'none' | 'always' | 'smart') ?? 'none');
        if (res.data.addressAlgorithms) {
          setAddressAlgorithms(prev => ({ ...prev, ...res.data.addressAlgorithms }));
        }
      } catch (err) {
        console.error('Failed to load shipment validation settings:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const parsedTlds = tldText
        .split(/[,\s]+/)
        .map(s => s.trim().replace(/^\./, '').toLowerCase())
        .filter(Boolean);
      const payload: ShipmentValidationData = { ...settings, allowedEmailTlds: parsedTlds };
      await Promise.all([
        api.put('/settings/shipment-validation', payload),
        api.put('/settings/address-merge', { addressMergeMode, addressAlgorithms }),
      ]);
      setSettings(payload);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-accent-primary" />
      </div>
    );
  }

  const allowedTldCount = tldText.split(/[,\s]+/).map(s => s.trim().replace(/^\./, '')).filter(Boolean).length;

  return (
    <div className="animate-fade-in">
      <PageHeader
        icon={ShieldCheck}
        title="Walidacja danych"
        subtitle="Inteligentne filtry ostrzegające przed błędami danych klienta zanim paczka trafi do kuriera. Ostrzeżenia, nie blokady - operator zawsze może wymusić nadanie."
      />

      <div className="space-y-6">
        {/* Guard przed duplikatem etykiet */}
        <div className="card p-5">
          <div className="flex items-start gap-4">
            <div className="p-2.5 rounded-xl bg-accent-primary/10 flex-shrink-0">
              <PackageIcon className="w-5 h-5 text-accent-primary" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-text-primary">Ostrzeżenie przy ponownym nadawaniu etykiet</h3>
                  <p className="text-xs text-text-muted mt-1">
                    Przy masowym nadawaniu: jeśli co najmniej N zaznaczonych zamówień ma już aktywny list przewozowy, pokaż dialog potwierdzenia zanim ruszy job.
                  </p>
                </div>
                <button
                  onClick={() => setSettings(prev => ({ ...prev, warnOnDuplicateLabels: !prev.warnOnDuplicateLabels }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ml-4 ${
                    settings.warnOnDuplicateLabels ? 'bg-accent-primary' : 'bg-surface-muted'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.warnOnDuplicateLabels ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              {settings.warnOnDuplicateLabels && (
                <div className="mt-4 flex items-center gap-3">
                  <label className="text-xs text-text-secondary">Próg (liczba paczek z listem):</label>
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={settings.duplicateLabelThreshold}
                    onChange={e => setSettings(prev => ({
                      ...prev,
                      duplicateLabelThreshold: Math.max(1, Math.min(100, parseInt(e.target.value) || 5)),
                    }))}
                    className="input w-24 text-sm"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Email typo detection */}
        <div className="card p-5">
          <div className="flex items-start gap-4">
            <div className="p-2.5 rounded-xl bg-accent-primary/10 flex-shrink-0">
              <AtSign className="w-5 h-5 text-accent-primary" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-text-primary">Wykrywanie literówek w emailu</h3>
                  <p className="text-xs text-text-muted mt-1">
                    Wyłapuje typowe pomyłki: <code className="text-accent-primary">gamail.com</code> → <code className="text-accent-primary">gmail.com</code>, <code className="text-accent-primary">gmail.combbgg</code> → <code className="text-accent-primary">gmail.com</code>, <code className="text-accent-primary">.pll</code> → <code className="text-accent-primary">.pl</code>, <code className="text-accent-primary">.coom</code> → <code className="text-accent-primary">.com</code>.
                  </p>
                </div>
                <button
                  onClick={() => setSettings(prev => ({ ...prev, emailTypoDetection: !prev.emailTypoDetection }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ml-4 ${
                    settings.emailTypoDetection ? 'bg-accent-primary' : 'bg-surface-muted'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.emailTypoDetection ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* TLD validation */}
        <div className="card p-5">
          <div className="flex items-start gap-4">
            <div className="p-2.5 rounded-xl bg-accent-primary/10 flex-shrink-0">
              <Globe className="w-5 h-5 text-accent-primary" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-text-primary">Walidacja rozszerzenia domeny (TLD)</h3>
                  <p className="text-xs text-text-muted mt-1">
                    Jeśli końcówka domeny nie jest na poniższej liście, pokaż ostrzeżenie. Nie blokuje nadania.
                  </p>
                </div>
                <button
                  onClick={() => setSettings(prev => ({ ...prev, emailTldValidation: !prev.emailTldValidation }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ml-4 ${
                    settings.emailTldValidation ? 'bg-accent-primary' : 'bg-surface-muted'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.emailTldValidation ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              {settings.emailTldValidation && (
                <div className="mt-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-xs text-text-secondary">Dozwolone TLD (oddziel przecinkami):</label>
                    <button
                      type="button"
                      onClick={() => setTldText(DEFAULT_EMAIL_TLDS.join(', '))}
                      className="text-xs text-accent-primary hover:text-accent-primary/80"
                    >
                      Przywróć domyślne
                    </button>
                  </div>
                  <textarea
                    value={tldText}
                    onChange={e => setTldText(e.target.value)}
                    className="input text-xs w-full font-mono"
                    rows={3}
                    placeholder="pl, com, eu, de, fr..."
                  />
                  <p className="text-xs text-text-disabled">
                    {allowedTldCount} wpisów. Lista domyślna: {DEFAULT_EMAIL_TLDS.length} rozszerzeń (polskie + EU top 10 + globalne + modern ecom).
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Phone smart validation */}
        <div className="card p-5">
          <div className="flex items-start gap-4">
            <div className="p-2.5 rounded-xl bg-accent-primary/10 flex-shrink-0">
              <Phone className="w-5 h-5 text-accent-primary" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-text-primary">Wykrywanie fałszywych numerów telefonu</h3>
                  <p className="text-xs text-text-muted mt-1">
                    Ostrzeżenie gdy numer jest oczywiście testowy (np. <code className="text-accent-primary">000000000</code>, <code className="text-accent-primary">123456789</code>, wszystkie cyfry identyczne). Podstawowy format (9 cyfr, +48) sprawdza walidacja InPost.
                  </p>
                </div>
                <button
                  onClick={() => setSettings(prev => ({ ...prev, phoneSmartValidation: !prev.phoneSmartValidation }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ml-4 ${
                    settings.phoneSmartValidation ? 'bg-accent-primary' : 'bg-surface-muted'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.phoneSmartValidation ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 px-3 py-2 bg-warning/10 border border-warning/20 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-warning flex-shrink-0" />
          <p className="text-xs text-warning">
            Ostrzeżenia pojawiają się jako "błędy walidacji" przy nadawaniu paczki. Operator może wymusić nadanie przyciskiem "Nadaj mimo to (pomiń walidację)" na karcie zamówienia.
          </p>
        </div>

        {/* R3.5 (2026-04-22): Address merge from WooCommerce - przeniesione z "Obsługi zamówień". */}
        <div className="card p-5">
          <div className="flex items-start gap-4 mb-4">
            <div className="p-2.5 rounded-xl bg-accent-primary/10 flex-shrink-0">
              <MapPin className="w-5 h-5 text-accent-primary" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-text-primary">Łączenie adresów z WooCommerce</h3>
              <p className="text-xs text-text-muted mt-1">
                WooCommerce przesyła adres w dwóch polach (Adres i Adres cd.). Klienci często wpisują numer domu w drugie pole, co powoduje brak numeru na etykietach InPost. Wybierz sposób łączenia tych pól.
              </p>
            </div>
          </div>
          <div className="space-y-3">
            {([
              { value: 'none' as const, label: 'Nie łącz', description: 'Zachowaj oba pola oddzielnie (domyślnie)' },
              { value: 'always' as const, label: 'Łącz zawsze', description: 'Zawsze dołącz Adres cd. do Adres (np. "Radosna" + "24" -> "Radosna 24")' },
              { value: 'smart' as const, label: 'Łącz inteligentnie', description: 'Dołącz z zaawansowaną korektą adresu - pomiń duplikaty, napraw formaty numerów, usuń zbędne treści' },
            ]).map((option) => (
              <div key={option.value}>
                <button
                  onClick={() => setAddressMergeMode(option.value)}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${
                    addressMergeMode === option.value
                      ? 'border-accent-primary bg-accent-primary/5'
                      : 'border-border hover:border-border-strong bg-surface-hover/30'
                  } ${addressMergeMode === option.value && option.value === 'smart' ? 'rounded-b-none' : ''}`}
                >
                  <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                    addressMergeMode === option.value
                      ? 'border-accent-primary bg-accent-primary'
                      : 'border-border'
                  }`} />
                  <div>
                    <p className={`font-medium ${addressMergeMode === option.value ? 'text-text-primary' : 'text-text-secondary'}`}>
                      {option.label}
                    </p>
                    <p className="text-xs text-text-muted mt-0.5">{option.description}</p>
                  </div>
                </button>

                {option.value === 'smart' && addressMergeMode === 'smart' && (
                  <div className="border-2 border-t-0 border-accent-primary rounded-b-xl bg-accent-primary/5 px-4 pb-4">
                    <div className="pt-3">
                      <button
                        onClick={() => setAlgorithmsExpanded(!algorithmsExpanded)}
                        className="flex items-center gap-2 text-sm text-accent-primary hover:text-accent-primary/80 transition-colors"
                      >
                        <svg className={`w-4 h-4 transition-transform ${algorithmsExpanded ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                        Konfiguracja algorytmów korekty adresu ({Object.values(addressAlgorithms).filter(Boolean).length}/16 aktywnych)
                      </button>
                      {algorithmsExpanded && (
                        <div className="mt-3 space-y-2">
                          {([
                            { id: 'autoSpace', name: 'Auto-spacja', desc: 'Dodaje brakującą spację między nazwą ulicy a numerem domu.', example: 'Sikorskiego79m1 \u2192 Sikorskiego 79m1' },
                            { id: 'normalizeSlashSpaces', name: 'Spacje przy slashu', desc: 'Usuwa zbędne spacje przed i po slashu w numerze domu/mieszkania.', example: 'Sikorskiego 13 /51 \u2192 Sikorskiego 13/51' },
                            { id: 'removeStrayPunctuation', name: 'Zbędne znaki', desc: 'Usuwa niepotrzebne znaki specjalne: cudzysłowy na końcu numeru, przecinki przed numerem domu.', example: 'PCK 11/20" \u2192 PCK 11/20, Krakowska, 94/1 \u2192 Krakowska 94/1' },
                            { id: 'deduplicateSlash', name: 'Deduplikacja numer/numer', desc: 'Usuwa zduplikowany numer, gdy obie strony ukośnika są identyczne. Z literą - zawsze. Bez litery - od 99 w górę (chroni 50/50, 70/70).', example: '479b/479b \u2192 479b, 100/100 \u2192 100, 50/50 \u2192 bez zmian' },
                            { id: 'deduplicateNumbers', name: 'Deduplikacja numerów', desc: 'Usuwa zduplikowane numery domu/mieszkania z pola Adres 2.', example: 'Szkolna 1/1 1 \u2192 Szkolna 1/1' },
                            { id: 'deduplicateFormat', name: 'Deduplikacja formatu', desc: 'Usuwa duplikaty zapisane w innym formacie.', example: 'Słodowa 1/m59 1m59 \u2192 Słodowa 1/m59' },
                            { id: 'removeCity', name: 'Usuwanie miasta z adresu', desc: 'Usuwa nazwę miasta powielona w polu adresu.', example: 'Osiedlowa 1B Jerzykowo (miasto: Jerzykowo) \u2192 Osiedlowa 1B' },
                            { id: 'removePostcode', name: 'Usuwanie kodu pocztowego', desc: 'Usuwa kod pocztowy powielony w adresie (z myślnikiem i bez).', example: 'Ścięgny 39/5 58540 (kod: 58-540) \u2192 Ścięgny 39/5' },
                            { id: 'removeDescriptive', name: 'Usuwanie opisu', desc: 'Usuwa zbędne opisy, np. "Mieszkanie w bloku".', example: 'Potyki 29/m3 Mieszkanie w bloku \u2192 Potyki 29/m3' },
                            { id: 'removeTrailingDot', name: 'Usuwanie kropki', desc: 'Usuwa kropkę na końcu numeru domu.', example: 'Miłosza 20. \u2192 Miłosza 20' },
                            { id: 'normalizeMNotation', name: 'Normalizacja "m"', desc: 'Zamienia zapis "m", "m." lub "/m" w numerze lokalu na format ze slashem. Obsługuje spację przed "m" i sufiks literowy.', example: '6m10 \u2192 6/10, 33 C m 7 \u2192 33C/7, 15 m. 40 \u2192 15/40' },
                            { id: 'addApartmentSlash', name: 'Slash między numerami', desc: 'Dodaje slash między numerem domu a mieszkania (spacja lub sklejenie).', example: '22 3 \u2192 22/3, 17c9 \u2192 17c/9' },
                            { id: 'stripLetterSlash', name: 'Usuwanie slasha przed literą', desc: 'Slash + pojedyncza litera \u2192 bez slasha (sufiks klatki, nie mieszkania).', example: 'Budziszynek 18/b \u2192 Budziszynek 18b' },
                            { id: 'stripTrailingSlash', name: 'Usuwanie zamykającego slasha', desc: 'Slash na końcu numeru budynku (brak mieszkania za nim).', example: 'Wyspiańskiego 26/ \u2192 Wyspiańskiego 26' },
                            { id: 'dotToSlash', name: 'Zamiana kropki na slash', desc: 'Kropka między dwiema liczbami \u2192 slash (typowy błąd zapisu numeru mieszkania).', example: 'Pułaskiego 15.11 \u2192 Pułaskiego 15/11' },
                            { id: 'dedupeFullPhrase', name: 'Deduplikacja powtórzonego adresu', desc: 'Cały adres powtórzony po przecinku/kropce/średniku \u2192 jedna kopia. Klient czasem wkleja adres dwa razy.', example: 'Radłówek 21, Radłówek 21 \u2192 Radłówek 21' },
                          ] as const).map((algo) => (
                            <label
                              key={algo.id}
                              className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                                addressAlgorithms[algo.id]
                                  ? 'bg-accent-primary/5 border border-accent-primary/20'
                                  : 'bg-surface-hover/20 border border-border-subtle hover:border-border'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={!!addressAlgorithms[algo.id]}
                                onChange={(e) => setAddressAlgorithms(prev => ({ ...prev, [algo.id]: e.target.checked }))}
                                className="mt-0.5 w-4 h-4 rounded border-border text-accent-primary focus:ring-accent-primary bg-surface-raised"
                              />
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium ${addressAlgorithms[algo.id] ? 'text-text-primary' : 'text-text-secondary'}`}>
                                  {algo.name}
                                </p>
                                <p className="text-xs text-text-muted mt-0.5">{algo.desc}</p>
                                <p className="text-xs text-accent-primary/60 mt-1 font-mono">{algo.example}</p>
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Sticky save bar */}
        <div className="sticky bottom-0 z-30 -mx-1 px-1 pt-4">
          <div className="flex items-center gap-3 p-4 rounded-xl bg-surface-raised/95 backdrop-blur-sm border border-border shadow-lg">
            <Button onClick={handleSubmit} variant="primary" size="sm" loading={saving} leftIcon={<Save />}>
              Zapisz ustawienia
            </Button>
            {saveStatus === 'success' && (
              <span className="flex items-center gap-1.5 text-xs text-success animate-fade-in">
                <CheckCircle className="w-3.5 h-3.5" /> Zapisano
              </span>
            )}
            {saveStatus === 'error' && (
              <span className="flex items-center gap-1.5 text-xs text-danger animate-fade-in">
                <AlertCircle className="w-3.5 h-3.5" /> Błąd zapisu
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Customer Blacklist Management (pkt N2, 2026-04-22) ───────

function BlacklistManagement() {
  const [entries, setEntries] = useState<Array<{ id: string; identifier: string; identifierType: 'email' | 'phone' | 'address'; displayName?: string | null; reason?: string | null; autoAdded: boolean; source?: string | null; createdAt: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newEntry, setNewEntry] = useState<{ identifier: string; identifierType: 'email' | 'phone' | 'address'; displayName: string; reason: string }>({
    identifier: '',
    identifierType: 'email',
    displayName: '',
    reason: '',
  });
  const [saving, setSaving] = useState(false);
  const confirmDialog = useConfirmDialog();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<{ entries: typeof entries }>('/blacklist');
      setEntries(res.data.entries);
    } catch (err) {
      console.error('Failed to load blacklist:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!newEntry.identifier.trim()) return;
    setSaving(true);
    try {
      await api.post('/blacklist', {
        identifier: newEntry.identifier.trim(),
        identifierType: newEntry.identifierType,
        displayName: newEntry.displayName.trim() || undefined,
        reason: newEntry.reason.trim() || undefined,
      });
      setShowAddModal(false);
      setNewEntry({ identifier: '', identifierType: 'email', displayName: '', reason: '' });
      load();
    } catch (err) {
      console.error('Failed to add blacklist entry:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (id: string, label: string) => {
    const ok = await confirmDialog({
      title: 'Usuń z czarnej listy',
      message: <>Czy na pewno chcesz usunąć wpis <strong>{label}</strong> z czarnej listy?</>,
      subtitle: 'Klient od razu będzie mógł ponownie zamawiać i brać udział w nadawaniu.',
      confirmLabel: 'Usuń wpis',
    });
    if (!ok) return;
    try {
      await api.delete(`/blacklist/${id}`);
      load();
    } catch (err) {
      console.error('Failed to remove entry:', err);
    }
  };

  const typeLabel = (t: string) => t === 'email' ? 'Email' : t === 'phone' ? 'Telefon' : 'Adres';
  const sourceLabel = (s: string | null | undefined, auto: boolean) => {
    if (!auto) return 'Dodane ręcznie';
    if (s === 'failed_deliveries') return 'Auto: zwroty';
    if (s === 'duplicate_orders') return 'Auto: duplikaty';
    return 'Automat';
  };

  return (
    <div className="animate-fade-in">
      <PageHeader
        icon={SkullIcon as unknown as LucideIcon}
        title="Czarna lista klientów"
        subtitle={<>Klienci, którzy nie biorą udziału w masowym nadawaniu paczek. Konfiguracja progów i zachowania: <Link to="/admin/settings/orders" className="text-accent-primary hover:underline">Ustawienia → Obsługa zamówień</Link>.</>}
        actions={
          <Button onClick={() => setShowAddModal(true)} variant="primary" size="sm" leftIcon={<Plus />}>
            Dodaj ręcznie
          </Button>
        }
      />

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-accent-primary" />
        </div>
      ) : entries.length === 0 ? (
        <EmptyState
          icon={SkullIcon as unknown as typeof Users}
          title="Czarna lista jest pusta"
          description="Możesz dodać klienta ręcznie albo pozwolić systemowi robić to automatycznie po zwrotach. Konfiguracja w Obsłudze zamówień."
          buttonLabel="Dodaj pierwszy wpis"
          onButtonClick={() => setShowAddModal(true)}
          buttonIcon={Plus}
        />
      ) : (
        <div className="card p-0 overflow-hidden">
          <table className="table w-full">
            <thead>
              <tr>
                <th className="!pl-4">Typ</th>
                <th>Identyfikator</th>
                <th>Nazwa klienta</th>
                <th>Powód</th>
                <th>Źródło</th>
                <th>Dodane</th>
                <th className="!w-16 text-center">Akcja</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(entry => (
                <tr key={entry.id} className="group">
                  <td className="!pl-4">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-surface-hover text-text-secondary">{typeLabel(entry.identifierType)}</span>
                  </td>
                  <td className="font-mono text-xs text-text-primary">{entry.identifier}</td>
                  <td className="text-sm text-text-secondary">{entry.displayName || '-'}</td>
                  <td className="text-xs text-text-muted max-w-xs truncate" title={entry.reason || ''}>{entry.reason || '-'}</td>
                  <td>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${entry.autoAdded ? 'bg-accent-tertiary/10 text-accent-tertiary' : 'bg-accent-primary/10 text-accent-primary'}`}>
                      {sourceLabel(entry.source, entry.autoAdded)}
                    </span>
                  </td>
                  <td className="text-xs text-text-muted">{new Date(entry.createdAt).toLocaleDateString('pl-PL')}</td>
                  <td className="!w-16">
                    <div className="flex items-center justify-center">
                      <button
                        onClick={() => handleRemove(entry.id, entry.displayName || entry.identifier)}
                        className="p-1.5 rounded text-text-muted hover:text-danger hover:bg-danger/10 transition-all"
                        title="Usuń z czarnej listy"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add modal */}
      {showAddModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => !saving && setShowAddModal(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-surface-raised border border-border shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-border">
              <h3 className="text-base font-semibold text-text-primary flex items-center gap-2">
                <SkullIcon className="w-5 h-5 text-accent-primary" />
                Dodaj na czarną listę
              </h3>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="block text-xs text-text-secondary mb-1">Typ identyfikatora</label>
                <select
                  value={newEntry.identifierType}
                  onChange={(e) => setNewEntry(prev => ({ ...prev, identifierType: e.target.value as 'email' | 'phone' | 'address' }))}
                  className="input w-full text-sm"
                >
                  <option value="email">Email</option>
                  <option value="phone">Telefon</option>
                  <option value="address">Adres</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">Wartość</label>
                <input
                  type="text"
                  value={newEntry.identifier}
                  onChange={(e) => setNewEntry(prev => ({ ...prev, identifier: e.target.value }))}
                  className="input w-full text-sm"
                  placeholder={newEntry.identifierType === 'email' ? 'klient@example.com' : newEntry.identifierType === 'phone' ? '500600700' : 'ul. Polna 5, 00-000 Warszawa'}
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">Nazwa klienta (opcjonalnie)</label>
                <input
                  type="text"
                  value={newEntry.displayName}
                  onChange={(e) => setNewEntry(prev => ({ ...prev, displayName: e.target.value }))}
                  className="input w-full text-sm"
                  placeholder="np. Jan Kowalski"
                />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">Powód (opcjonalnie)</label>
                <textarea
                  value={newEntry.reason}
                  onChange={(e) => setNewEntry(prev => ({ ...prev, reason: e.target.value }))}
                  className="input w-full text-sm"
                  rows={2}
                  placeholder="np. 3 razy nie odebrał paczki"
                />
              </div>
            </div>
            <div className="p-4 border-t border-border flex items-center gap-2">
              <Button onClick={handleAdd} disabled={!newEntry.identifier.trim()} variant="primary" size="sm" loading={saving} leftIcon={<Plus />}>
                Dodaj do listy
              </Button>
              <Button onClick={() => setShowAddModal(false)} disabled={saving} variant="secondary" size="sm" className="ml-auto">
                Anuluj
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Settings Overview (Landing Page) ─────────────────────────
// #R7-6 - ujednolicony jeden kolor ikon (text-accent-primary). Wcześniej każda
// sekcja miała inny accent (info, secondary, tertiary, warning) - user zgłosił
// że to wygląda chaotycznie. Spójny pattern z ustawieniami Wiadomości.
const settingsSections = [
  {
    title: 'Ogólne',
    description: 'Kolejność sklepów, ikony paska narzędzi, widoczność statusu nieprzypisanego',
    href: '/admin/settings/general',
    icon: Sliders,
    color: 'text-accent-primary',
    bgColor: 'bg-accent-primary/10',
  },
  {
    title: 'Obsługa zamówień',
    description: 'Automatyczna synchronizacja WooCommerce, synchronizacja zamówień ręcznych do sklepu',
    href: '/admin/settings/orders',
    icon: ClipboardList,
    color: 'text-accent-primary',
    bgColor: 'bg-accent-primary/10',
  },
  {
    title: 'Panel klienta',
    description: 'Funkcje panelu, ponowna wysyłka, branding, reklamacje, faktury, edycja adresu',
    href: '/admin/settings/portal',
    icon: Users,
    color: 'text-accent-primary',
    bgColor: 'bg-accent-primary/10',
  },
  {
    title: 'Reklamacje',
    description: 'Automatyczne zwroty Przelewy24, powiadomienia email o zmianach statusu',
    href: '/admin/settings/returns',
    icon: RotateCcw,
    color: 'text-accent-primary',
    bgColor: 'bg-accent-primary/10',
  },
  {
    title: 'Walidacja danych',
    description: 'Literówki email, walidacja TLD, fałszywe numery, guard duplikatu etykiet, łączenie adresów z WooCommerce',
    href: '/admin/settings/validation',
    icon: ShieldCheck,
    color: 'text-accent-primary',
    bgColor: 'bg-accent-primary/10',
  },
  {
    title: 'Powiadomienia email',
    description: 'Szablony emaili wysyłanych automatycznie do klientów przy zmianach statusów zamówień',
    href: '/admin/settings/email-templates',
    icon: Mail,
    color: 'text-accent-primary',
    bgColor: 'bg-accent-primary/10',
  },
];

function SettingsOverview() {
  return (
    <div className="animate-fade-in">
      <PageHeader icon={Settings} title="Ustawienia" subtitle="Zarządzaj konfiguracją aplikacji" />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {settingsSections.map((section) => (
          <Link
            key={section.href}
            to={section.href}
            className="card hover:border-accent-primary/30 hover:shadow-accent-primary/20/5 transition-all group"
          >
            <div className="flex items-start gap-4">
              <div className={`p-3 rounded-xl ${section.bgColor} flex-shrink-0`}>
                <section.icon className={`w-5 h-5 ${section.color}`} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-text-primary group-hover:text-accent-primary transition-colors">
                  {section.title}
                </h3>
                <p className="text-xs text-text-muted mt-1 leading-relaxed">
                  {section.description}
                </p>
              </div>
              <ChevronRight className="w-4 h-4 text-text-disabled group-hover:text-accent-primary transition-colors flex-shrink-0 mt-1" />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ─── Email Templates Settings (moved from EmailSettingsPage) ────────────────
function EmailTemplatesSettings() {
  const confirmDialog = useConfirmDialog();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [stores, setStores] = useState<StoreType[]>([]);
  const [initializingFor, setInitializingFor] = useState<string | null>(null); // storeId or 'general'
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [creatingForStore, setCreatingForStore] = useState<string | null | undefined>(undefined); // undefined=closed, null=general, string=storeId
  const [showPreview, setShowPreview] = useState(false);
  const [preview, setPreview] = useState<{ subject: string; bodyHtml: string } | null>(null);

  // Form
  const [formName, setFormName] = useState('');
  const [formSlug, setFormSlug] = useState('');
  const [formSubject, setFormSubject] = useState('');
  const [formBodyHtml, setFormBodyHtml] = useState('');
  const [formIsActive, setFormIsActive] = useState(true);
  const [formStoreId, setFormStoreId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const quillModules = useMemo(() => withPlainTextPaste({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean'],
    ],
  }), []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [templatesRes, storesRes] = await Promise.all([
        emailApi.getTemplates(),
        storeApi.getAll(),
      ]);
      setTemplates(templatesRes.data.templates);
      setStores(storesRes.data);
    } catch (err) {
      console.error('[EmailTemplatesSettings] Load error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showPreview) setShowPreview(false);
        else resetForm();
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [showPreview]);

  const generateSlug = (name: string) =>
    name.toLowerCase()
      .replace(/[ąćęłńóśźż]/g, c => ({ ą:'a',ć:'c',ę:'e',ł:'l',ń:'n',ó:'o',ś:'s',ź:'z',ż:'z' }[c] || c))
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');

  const resetForm = () => {
    setEditingTemplate(null);
    setCreatingForStore(undefined);
    setFormName('');
    setFormSlug('');
    setFormSubject('');
    setFormBodyHtml('');
    setFormIsActive(true);
    setFormStoreId(null);
    setFormError('');
  };

  const openCreate = (storeId: string | null) => {
    resetForm();
    setCreatingForStore(storeId);
    setFormStoreId(storeId);
  };

  const openEdit = (template: EmailTemplate) => {
    setCreatingForStore(undefined);
    setEditingTemplate(template);
    setFormName(template.name);
    setFormSlug(template.slug);
    setFormSubject(template.subject);
    setFormBodyHtml(template.bodyHtml);
    setFormIsActive(template.isActive);
    setFormStoreId(template.storeId || null);
  };

  const handleSave = async () => {
    if (!formName || !formSlug || !formSubject || !formBodyHtml) {
      setFormError('Wypełnij wszystkie wymagane pola');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      if (editingTemplate) {
        await emailApi.updateTemplate(editingTemplate.id, {
          name: formName, slug: formSlug, subject: formSubject,
          bodyHtml: formBodyHtml, isActive: formIsActive, storeId: formStoreId,
        });
      } else {
        await emailApi.createTemplate({
          name: formName, slug: formSlug, subject: formSubject,
          bodyHtml: formBodyHtml, isActive: formIsActive, storeId: formStoreId,
        });
      }
      resetForm();
      loadData();
    } catch (err: any) {
      setFormError(err.response?.data?.error || 'Błąd podczas zapisywania');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!(await confirmDialog({
      title: 'Usuń szablon',
      message: 'Czy na pewno chcesz usunąć ten szablon?',
      confirmLabel: 'Usuń',
    }))) return;
    try {
      await emailApi.deleteTemplate(id);
      if (editingTemplate?.id === id) resetForm();
      loadData();
    } catch (err) {
      console.error('Error deleting template:', err);
    }
  };

  const handleDuplicate = async (id: string) => {
    try {
      await emailApi.duplicateTemplate(id);
      loadData();
    } catch (err) {
      console.error('Error duplicating template:', err);
    }
  };

  const handleInit = async (storeId?: string) => {
    const key = storeId || 'general';
    setInitializingFor(key);
    try {
      await emailApi.initTemplates(storeId);
      loadData();
    } catch (err) {
      console.error('Error initializing templates:', err);
    } finally {
      setInitializingFor(null);
    }
  };

  const handlePreview = async (template: EmailTemplate) => {
    try {
      const { data } = await emailApi.previewTemplate(template.id);
      setPreview(data.preview);
      setShowPreview(true);
    } catch {
      setPreview({ subject: template.subject, bodyHtml: template.bodyHtml });
      setShowPreview(true);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-accent-primary" />
      </div>
    );
  }

  const isFormOpen = editingTemplate || creatingForStore !== undefined;
  const generalTemplates = templates.filter(t => !t.storeId);
  const storeGroups = stores.map(store => ({
    store,
    templates: templates.filter(t => t.storeId === store.id),
  }));

  // ─── Inline editor form ──────────────────────
  const renderForm = () => {
    if (!isFormOpen) return null;
    return (
      <div className="card p-5 space-y-4 animate-fade-in">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-text-primary">
            {editingTemplate ? 'Edytuj szablon' : 'Nowy szablon'}
          </h3>
          <button onClick={resetForm} className="text-text-secondary hover:text-text-primary">
            <X className="w-5 h-5" />
          </button>
        </div>

        {formError && (
          <div className="flex items-center gap-2 p-3 bg-danger/10 text-danger rounded-lg text-sm">
            <XCircle className="w-4 h-4" /> {formError}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="label">Nazwa *</label>
            <input
              type="text" value={formName}
              onChange={(e) => { setFormName(e.target.value); if (!editingTemplate) setFormSlug(generateSlug(e.target.value)); }}
              placeholder="np. Zamówienie wysłane" className="input text-sm"
            />
          </div>
          <div>
            <label className="label">Slug *</label>
            <input
              type="text" value={formSlug}
              onChange={(e) => setFormSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
              placeholder="np. order-shipped" className="input text-sm"
            />
          </div>
          <div>
            <label className="label">Sklep</label>
            <select
              value={formStoreId || ''}
              onChange={(e) => setFormStoreId(e.target.value || null)}
              className="input text-sm"
            >
              <option value="">Ogólny (wszystkie)</option>
              {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="label">Temat wiadomości *</label>
          <input
            type="text" value={formSubject}
            onChange={(e) => setFormSubject(e.target.value)}
            placeholder="np. Twoje zamówienie {{numer_zamowienia}} zostało wysłane"
            className="input text-sm"
          />
          <p className="text-xs text-text-muted mt-1">
            Zmienne: {'{{imie}}'}, {'{{nazwisko}}'}, {'{{numer_zamowienia}}'}, {'{{wartosc}}'}, {'{{tracking}}'}, {'{{link_sledzenia}}'}, {'{{sklep}}'}, {'{{konto_bankowe}}'}
          </p>
        </div>

        <div>
          <label className="label">Treść wiadomości *</label>
          <div className="bg-white rounded-lg overflow-hidden">
            <ReactQuill
              theme="snow" value={formBodyHtml} onChange={setFormBodyHtml}
              modules={quillModules}
              placeholder="Wpisz treść wiadomości..."
              className="email-editor"
            />
          </div>
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox" checked={formIsActive}
            onChange={(e) => setFormIsActive(e.target.checked)}
            className="w-4 h-4 rounded border-border bg-surface-hover text-accent-primary focus:ring-accent-primary"
          />
          <span className="text-sm text-text-primary">Szablon aktywny</span>
        </label>

        <div className="flex items-center gap-3 pt-2 border-t border-border">
          <Button onClick={handleSave} variant="primary" size="sm" loading={saving} leftIcon={<Save />}>
            {editingTemplate ? 'Zapisz zmiany' : 'Utwórz szablon'}
          </Button>
          <Button onClick={resetForm} variant="secondary" size="sm">Anuluj</Button>
        </div>
      </div>
    );
  };

  // ─── Template card ──────────────────────
  const renderCard = (template: EmailTemplate, colorClass = 'text-accent-primary') => (
    <div key={template.id} className="p-4 bg-surface-hover/30 rounded-lg hover:bg-surface-hover/50 transition-colors">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <Mail className={`w-4 h-4 ${colorClass} flex-shrink-0`} />
          <h4 className="font-medium text-text-primary text-sm truncate">{template.name}</h4>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {template.isSystem && (
            <span className="text-xxs px-1.5 py-0.5 bg-accent-secondary/20 text-accent-secondary rounded">System</span>
          )}
          {!template.isActive && (
            <span className="text-xxs px-1.5 py-0.5 bg-warning/20 text-warning rounded">Wył.</span>
          )}
        </div>
      </div>
      <p className="text-xs text-text-secondary mb-3 line-clamp-1">{template.subject}</p>
      <div className="flex items-center gap-1 pt-2 border-t border-border-subtle">
        <Button onClick={() => openEdit(template)} variant="secondary" size="sm" leftIcon={<Edit3 />} fullWidth>
          Edytuj
        </Button>
        <button onClick={() => handlePreview(template)} className="p-1.5 text-text-secondary hover:text-text-primary transition-colors" title="Podgląd">
          <Eye className="w-3.5 h-3.5" />
        </button>
        <button onClick={() => handleDuplicate(template.id)} className="p-1.5 text-text-secondary hover:text-text-primary transition-colors" title="Duplikuj">
          <Copy className="w-3.5 h-3.5" />
        </button>
        <button onClick={() => handleDelete(template.id)} className="p-1.5 text-text-secondary hover:text-danger transition-colors" title="Usuń">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="animate-fade-in space-y-6">
      <PageHeader icon={Mail} title="Powiadomienia email" subtitle="Szablony emaili wysyłanych automatycznie do klientów przy zmianach statusów zamówień" />

      {/* Inline form */}
      {renderForm()}

      {/* General templates */}
      <div className="card overflow-hidden">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-accent-primary/20">
              <Globe className="w-5 h-5 text-accent-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-text-primary">Szablony ogólne</h3>
              <p className="text-xs text-text-muted">Dostępne dla wszystkich sklepów</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {generalTemplates.length === 0 && (
              <Button
                onClick={() => handleInit()}
                variant="secondary"
                size="sm"
                loading={initializingFor === 'general'}
                leftIcon={<FileText />}
              >
                Wczytaj domyślne
              </Button>
            )}
            <Button onClick={() => openCreate(null)} variant="primary" size="sm" leftIcon={<Plus />}>
              Nowy
            </Button>
          </div>
        </div>

        <div className="px-4 pb-4">
          {generalTemplates.length === 0 ? (
            <div className="bg-surface-hover/30 rounded-lg">
              <EmptyState
                icon={FileText}
                title="Brak szablonów ogólnych"
                description='Kliknij "Wczytaj domyślne" aby dodać predefiniowane szablony lub utwórz własny'
                size="sm"
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {generalTemplates.map(t => renderCard(t, 'text-accent-primary'))}
            </div>
          )}
        </div>
      </div>

      {/* Per-store templates */}
      {storeGroups.map(({ store, templates: storeTemplates }) => (
        <div key={store.id} className="card overflow-hidden">
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-accent-info/20">
                <Store className="w-5 h-5 text-accent-info" />
              </div>
              <div>
                <h3 className="font-semibold text-text-primary">{store.name}</h3>
                <p className="text-xs text-text-muted">Szablony dedykowane dla tego sklepu</p>
              </div>
            </div>
            <Button onClick={() => openCreate(store.id)} variant="primary" size="sm" leftIcon={<Plus />}>
              Nowy
            </Button>
          </div>

          <div className="px-4 pb-4">
            {storeTemplates.length === 0 ? (
              <div className="bg-surface-hover/30 rounded-lg">
                <EmptyState
                  icon={FileText}
                  title="Brak szablonów"
                  description="Utwórz dedykowany szablon dla tego sklepu"
                  size="sm"
                />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {storeTemplates.map(t => renderCard(t, 'text-accent-info'))}
              </div>
            )}
          </div>
        </div>
      ))}

      {/* Preview modal */}
      {showPreview && preview && (
        <div className="fixed inset-0 bg-black/70 flex items-start justify-center pt-16 z-[60] p-4 overflow-y-auto">
          <div className="bg-surface-raised rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h4 className="font-semibold text-text-primary">Podgląd szablonu</h4>
              <button onClick={() => setShowPreview(false)} className="text-text-secondary hover:text-text-primary">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4 overflow-y-auto max-h-[calc(80vh-60px)]">
              <div>
                <p className="text-xs text-text-muted mb-1">Temat:</p>
                <p className="text-text-primary">{preview.subject}</p>
              </div>
              <div>
                <p className="text-xs text-text-muted mb-1">Treść:</p>
                <div className="bg-white rounded-lg p-4 text-black prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: preview.bodyHtml }} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function SettingsPage() {
  const location = useLocation();
  const isOverview = location.pathname === '/admin/settings';

  return (
    <div>
      {/* Breadcrumb for sub-pages */}
      {!isOverview && (
        <div className="mb-6 animate-fade-in">
          <Link
            to="/admin/settings"
            className="inline-flex items-center text-sm text-text-secondary hover:text-accent-primary transition-colors"
          >
            <ChevronRight className="w-4 h-4 rotate-180 mr-1" />
            Powrót do ustawień
          </Link>
        </div>
      )}

      <Routes>
        <Route index element={<SettingsOverview />} />
        <Route path="general" element={<GeneralSettings />} />
        <Route path="orders" element={<OrderHandlingSettings />} />
        <Route path="sync" element={<OrderHandlingSettings />} />
        <Route path="portal" element={<PortalSettings />} />
        <Route path="resend" element={<PortalSettings />} />
        <Route path="returns" element={<ReturnsSettings />} />
        <Route path="validation" element={<ValidationSettings />} />
        <Route path="blacklist" element={<BlacklistManagement />} />
        <Route path="email-templates" element={<EmailTemplatesSettings />} />
      </Routes>
    </div>
  );
}
